import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance } from '../App';
import { LogOut, CheckCircle, XCircle, Eye, Trash2, Copy, Clock, RefreshCw, X, ShieldOff, Calendar, Building2, Star, MessageSquare, AlertTriangle, FileText, ShieldCheck, Users, Phone, Mail, Heart, ExternalLink, Settings, Languages, Lock, Percent, CreditCard, ShieldAlert, QrCode, History, KeyRound, UserCog, DatabaseBackup, Tag } from 'lucide-react';
import ChangeEmailModal, { sendAdminPasswordReset, toastForReset } from '../components/admin/ChangeEmailModal';
import BusinessQRModal from '../components/BusinessQRModal';
import { formatPlaceName } from '../utils/placeName';
import AdminOutreach from './AdminOutreach';
import AdminClassifieds from './AdminClassifieds';
import AdminInvoices from './AdminInvoices';
import AdminReferrals from './AdminReferrals';
import AdminUsers from './AdminUsers';
import AdminIssueLog from './AdminIssueLog';
import AdminErrorLog from './AdminErrorLog';
import AdminRegressionLog from './AdminRegressionLog';
import AdminOffersTab from './AdminOffersTab';
import AdminPricePromotions from './AdminPricePromotions';
import SwQueueHealthWidget from '../components/SwQueueHealthWidget';
import PossibleDuplicatesWidget from '../components/admin/PossibleDuplicatesWidget';
import WeeklyHoursEditor from '../components/WeeklyHoursEditor';
import { ClosureSection } from './dashboard/ClosureSection';
import { useFlags } from '../hooks/useFlags';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { logout, user } = useAuth();
  const flags = useFlags();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  // Feb 2026 — recovery from Business Management. `changeEmailTarget` holds
  // the linked-owner user + business context for the shared modal. Email is
  // masked by default; admin can reveal individually per row.
  const [changeEmailTarget, setChangeEmailTarget] = useState(null);
  const [revealedOwnerIds, setRevealedOwnerIds] = useState(new Set());
  const toggleOwnerReveal = (userId) => {
    setRevealedOwnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };
  const flashMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  const sendBusinessLoginReset = async (business) => {
    const owner = business.linkedOwner;
    if (!owner || !owner.hasAccount) return;
    if (!window.confirm(`Send a password-reset link to the owner account for "${business.businessName}"?`)) return;
    setActionLoading(`biz-reset-${business.id}`);
    try {
      const data = await sendAdminPasswordReset(owner.userId);
      const toast = toastForReset(data, owner.emailMasked);
      flashMessage(toast.level, toast.message);
    } catch (err) {
      flashMessage('error', err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setActionLoading(null);
    }
  };

  const [confirmAction, setConfirmAction] = useState(null);
  // Trial Test Mode — list of business IDs owned by TRIAL_TEST_MODE_EMAIL
  // that are safe to fast-forward, plus the mode status for the toolbar.
  const [trialTestStatus, setTrialTestStatus] = useState({ enabled: false, testEmail: '', businessIds: [] });
  const [trialFastForwardBiz, setTrialFastForwardBiz] = useState(null); // business being fast-forwarded (modal)
  const [trialFastForwardMinutes, setTrialFastForwardMinutes] = useState(30);
  const [trialFastForwardLoading, setTrialFastForwardLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  // Iter 197 — QR modal state for the Businesses tab actions.
  const [qrBusiness, setQrBusiness] = useState(null);

  // Event management state
  const [activeTab, setActiveTab] = useState('businesses'); // 'businesses' | 'events' | 'reviews' | 'reports' | 'claims' | 'outreach'
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState('pending');
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventConfirm, setEventConfirm] = useState(null);
  const [eventEditModal, setEventEditModal] = useState(null);
  const [eventRejectReason, setEventRejectReason] = useState('');
  const [allEventsForBadge, setAllEventsForBadge] = useState([]);
  
  // Review moderation state
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportStatusFilter, setReportStatusFilter] = useState('open');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [openReportsCount, setOpenReportsCount] = useState(0);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');

  // Source filter for businesses
  const [sourceFilter, setSourceFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [claimedFilter, setClaimedFilter] = useState('');

  // Pagination state (shared pattern)
  const [pagination, setPagination] = useState({
    businesses: { page: 1, totalPages: 1, total: 0 },
    events: { page: 1, totalPages: 1, total: 0 },
    reviews: { page: 1, totalPages: 1, total: 0 },
    reports: { page: 1, totalPages: 1, total: 0 },
    claims: { page: 1, totalPages: 1, total: 0 },
    charities: { page: 1, totalPages: 1, total: 0 },
  });
  const PAGE_SIZE = 25;

  // Claims state
  const [claims, setClaims] = useState([]);
  const [claimFilter, setClaimFilter] = useState('pending');
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
  const [claimConfirm, setClaimConfirm] = useState(null);
  const [claimAdminNotes, setClaimAdminNotes] = useState('');

  // Local Safety Bulletin pending count (for the header badge link)
  const [pendingSafetyCount, setPendingSafetyCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axiosInstance.get('/admin/safety-bulletin/pending-count');
        if (!cancelled) setPendingSafetyCount(r.data?.pendingCount || 0);
      } catch { /* non-fatal */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const [claimInfoRequests, setClaimInfoRequests] = useState([]);

  // Charity state
  const [charityApps, setCharityApps] = useState([]);
  const [charityFilter, setCharityFilter] = useState('pending');
  const [charityLoading, setCharityLoading] = useState(false);
  const [pendingCharityCount, setPendingCharityCount] = useState(0);
  const [charityConfirm, setCharityConfirm] = useState(null);
  const [charityAdminNotes, setCharityAdminNotes] = useState('');

  // Admin config state
  const [showSeededInFeed, setShowSeededInFeed] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  // Founding-free migration
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migratePreview, setMigratePreview] = useState(null);

  // Create Seeded Business state
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [seedForm, setSeedForm] = useState({ businessName: '', description: '', category: 'Services', city: '', phone: '', email: '', address: '' });
  const [seedSubmitting, setSeedSubmitting] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchFollowMetrics();
    fetchAllEventsCount();
    fetchPendingReviewsCount();
    fetchOpenReportsCount();
    fetchPendingClaimsCount();
    fetchPendingCharityCount();
    fetchTrialTestStatus();
    if (activeTab === 'businesses') {
      fetchBusinesses();
    } else if (activeTab === 'events') {
      fetchEvents();
    } else if (activeTab === 'reviews') {
      fetchReviews();
    } else if (activeTab === 'reports') {
      fetchReports();
    } else if (activeTab === 'claims') {
      fetchClaims();
    } else if (activeTab === 'charities') {
      fetchCharityApps();
    }
  }, [filter, activeTab, eventFilter, reviewFilter, reportStatusFilter, reportTypeFilter, sourceFilter, claimFilter, charityFilter, pagination.businesses.page, pagination.events.page, pagination.reviews.page, pagination.reports.page, pagination.claims.page, pagination.charities.page, cityFilter, categoryFilter, contactFilter, claimedFilter]);

  const [followMetrics, setFollowMetrics] = useState(null);

  const fetchFollowMetrics = async () => {
    try {
      const response = await axiosInstance.get('/admin/dashboard/follow-metrics');
      setFollowMetrics(response.data);
    } catch (error) {
      // Non-fatal — card just won't render
      console.warn('follow-metrics fetch failed:', error?.response?.data || error.message);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTrialTestStatus = async () => {
    try {
      const [statusRes, eligibleRes] = await Promise.all([
        axiosInstance.get('/admin/trial/test-mode-status'),
        axiosInstance.get('/admin/trial/eligible-businesses'),
      ]);
      const businessIds = (eligibleRes.data?.businesses || []).map(b => b.id);
      setTrialTestStatus({
        enabled: !!statusRes.data?.enabled,
        testEmail: statusRes.data?.testEmail || '',
        businessIds,
      });
    } catch (error) {
      // Non-fatal — keep defaults
      console.warn('Trial test-mode status fetch failed:', error?.response?.data || error.message);
    }
  };

  const fetchAdminConfig = async () => {
    try {
      const res = await axiosInstance.get('/admin/config');
      setShowSeededInFeed(res.data.showSeededInFeed || false);
    } catch (err) {
      console.error('Config fetch error:', err);
    }
  };

  const toggleShowSeeded = async () => {
    setConfigLoading(true);
    try {
      const newVal = !showSeededInFeed;
      await axiosInstance.put('/admin/config', { showSeededInFeed: newVal });
      setShowSeededInFeed(newVal);
      setMessage({ type: 'success', text: newVal ? 'Seeded businesses now visible in public feed' : 'Seeded businesses hidden from public feed' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update config' });
    } finally {
      setConfigLoading(false);
    }
  };

  const runFoundingFreeMigration = async (dryRun) => {
    setMigrateBusy(true);
    try {
      const res = await axiosInstance.post('/admin/businesses/migrate-founding-free', { dryRun });
      if (dryRun) {
        setMigratePreview(res.data.wouldMigrate);
        setMessage({ type: 'success', text: `${res.data.wouldMigrate} approved business(es) would be moved to the free founders plan.` });
      } else {
        setMigratePreview(0);
        setMessage({ type: 'success', text: `Migrated ${res.data.migrated} business(es) to the free 12-month founders plan.` });
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Migration failed' });
    } finally {
      setMigrateBusy(false);
    }
  };


  useEffect(() => { fetchAdminConfig(); }, []);

  const handleCreateSeeded = async () => {
    if (!seedForm.businessName.trim()) {
      setMessage({ type: 'error', text: 'Business name is required' });
      return;
    }
    setSeedSubmitting(true);
    try {
      const res = await axiosInstance.post('/admin/business/seed', seedForm);
      setMessage({ type: 'success', text: `Created: ${res.data.businessName} (ID: ${res.data.id})` });
      setSeedForm({ businessName: '', description: '', category: 'Services', city: '', phone: '', email: '', address: '' });
      setShowSeedForm(false);
      fetchBusinesses();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create' });
    } finally {
      setSeedSubmitting(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.businesses.page, limit: PAGE_SIZE };
      if (filter === 'trash') {
        params.deleted = 'only';
      } else if (filter !== 'all') {
        params.approval_status = filter;
      }
      if (sourceFilter) params.source = sourceFilter;
      if (searchTerm) params.search = searchTerm;
      if (cityFilter) params.city = cityFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (contactFilter) params.contact_filter = contactFilter;
      if (claimedFilter) params.claimed = claimedFilter;
      const response = await axiosInstance.get('/admin/businesses', { params });
      const data = response.data;
      setBusinesses(data.items || []);
      setPagination(p => ({ ...p, businesses: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setMessage({ type: 'error', text: 'Failed to fetch businesses' });
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setEventsLoading(true);
      const params = { page: pagination.events.page, limit: PAGE_SIZE };
      if (eventFilter !== 'all') params.status = eventFilter;
      const response = await axiosInstance.get('/admin/events', { params });
      const data = response.data;
      setEvents(data.items || []);
      setPagination(p => ({ ...p, events: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching events:', error);
      setMessage({ type: 'error', text: 'Failed to fetch events' });
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchAllEventsCount = async () => {
    try {
      const response = await axiosInstance.get('/admin/events', { params: { status: 'pending' } });
      setAllEventsForBadge(response.data);
    } catch (error) {
      console.error('Error fetching event count:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const params = { page: pagination.reviews.page, limit: PAGE_SIZE };
      if (reviewFilter !== 'all') params.status = reviewFilter;
      const response = await axiosInstance.get('/admin/reviews', { params });
      const data = response.data;
      setReviews(data.items || []);
      setPagination(p => ({ ...p, reviews: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchPendingReviewsCount = async () => {
    try {
      const response = await axiosInstance.get('/admin/reviews', { params: { status: 'pending', page: 1, limit: 1 } });
      setPendingReviewsCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching review count:', error);
    }
  };

  const handleReviewApprove = async (reviewId) => {
    setActionLoading(`approve-review-${reviewId}`);
    try {
      await axiosInstance.put(`/admin/review/${reviewId}/approve`);
      setMessage({ type: 'success', text: 'Review approved' });
      await fetchReviews();
      await fetchPendingReviewsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleReviewReject = async (reviewId) => {
    setActionLoading(`reject-review-${reviewId}`);
    try {
      await axiosInstance.put(`/admin/review/${reviewId}/reject`, { reason: 'Inappropriate content' });
      setMessage({ type: 'success', text: 'Review rejected' });
      await fetchReviews();
      await fetchPendingReviewsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Report functions
  const fetchReports = async () => {
    try {
      setReportsLoading(true);
      const params = { page: pagination.reports.page, limit: PAGE_SIZE };
      if (reportStatusFilter !== 'all') params.status = reportStatusFilter;
      if (reportTypeFilter !== 'all') params.targetType = reportTypeFilter;
      const response = await axiosInstance.get('/admin/reports', { params });
      const data = response.data;
      setReports(data.items || []);
      setPagination(p => ({ ...p, reports: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  const fetchOpenReportsCount = async () => {
    try {
      const response = await axiosInstance.get('/admin/reports', { params: { status: 'open', page: 1, limit: 1 } });
      setOpenReportsCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching reports count:', error);
    }
  };

  const handleReportResolve = async (reportId) => {
    setActionLoading(`resolve-report-${reportId}`);
    try {
      await axiosInstance.put(`/admin/report/${reportId}/resolve`, { adminNotes: notesText || '' });
      setMessage({ type: 'success', text: t('reportResolved') });
      setEditingNotes(null);
      setNotesText('');
      await fetchReports();
      await fetchOpenReportsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleReportDismiss = async (reportId) => {
    setActionLoading(`dismiss-report-${reportId}`);
    try {
      await axiosInstance.put(`/admin/report/${reportId}/dismiss`, { adminNotes: notesText || '' });
      setMessage({ type: 'success', text: t('reportDismissed') });
      setEditingNotes(null);
      setNotesText('');
      await fetchReports();
      await fetchOpenReportsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleSaveNotes = async (reportId) => {
    setActionLoading(`notes-report-${reportId}`);
    try {
      await axiosInstance.put(`/admin/report/${reportId}/notes`, { adminNotes: notesText });
      setMessage({ type: 'success', text: t('notesUpdated') });
      setEditingNotes(null);
      await fetchReports();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const getReportReasonLabel = (reason) => t(`reportReason_${reason}`);

  // Claims functions
  const fetchClaims = async () => {
    try {
      setClaimsLoading(true);
      const params = { page: pagination.claims.page, limit: PAGE_SIZE };
      if (claimFilter !== 'all') params.status = claimFilter;
      const response = await axiosInstance.get('/admin/claims', { params });
      const data = response.data;
      setClaims(data.items || []);
      setPagination(p => ({ ...p, claims: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setClaimsLoading(false);
    }
  };

  const fetchPendingClaimsCount = async () => {
    try {
      const response = await axiosInstance.get('/admin/claims/count');
      setPendingClaimsCount(response.data.count);
    } catch (error) {
      console.error('Error fetching claims count:', error);
    }
  };

  // Charity functions
  const fetchCharityApps = async () => {
    try {
      setCharityLoading(true);
      const params = { page: pagination.charities.page, limit: PAGE_SIZE };
      params.status = charityFilter === 'all' ? 'all' : charityFilter;
      const response = await axiosInstance.get('/admin/charity-applications', { params });
      const data = response.data;
      setCharityApps(data.items || []);
      setPagination(p => ({ ...p, charities: { page: data.page, totalPages: data.totalPages, total: data.total } }));
    } catch (error) {
      console.error('Error fetching charity apps:', error);
    } finally {
      setCharityLoading(false);
    }
  };

  const fetchPendingCharityCount = async () => {
    try {
      const response = await axiosInstance.get('/admin/charity-applications/count');
      setPendingCharityCount(response.data.count);
    } catch (error) {
      console.error('Error fetching charity count:', error);
    }
  };

  const handleCharityApprove = async (appId, opts = {}) => {
    setActionLoading(`approve-charity-${appId}`);
    try {
      await axiosInstance.put(`/admin/charity-application/${appId}/approve`, {
        adminNotes: charityAdminNotes,
        confirmedNoDuplicate: !!opts.confirmedNoDuplicate,
      });
      setMessage({ type: 'success', text: 'Application approved - listing created' });
      setCharityConfirm(null);
      setCharityAdminNotes('');
      await fetchCharityApps();
      await fetchPendingCharityCount();
    } catch (error) {
      // Iter 192 — Hard duplicate guard at approval. Admin must explicitly
      // confirm that the near-duplicate is a different organisation.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.error === 'duplicate_business') {
        const match = detail.match || {};
        const matchLabel = `${match.businessName || ''}${match.city ? ' — ' + match.city : ''}`.trim();
        const ok = window.confirm(
          `A similar listing already exists: "${matchLabel}".\n\nApprove anyway and create a separate listing? Click OK only if this is genuinely a different organisation.`
        );
        if (ok) {
          await handleCharityApprove(appId, { confirmedNoDuplicate: true });
          return;
        }
        setMessage({ type: 'error', text: 'Approval cancelled — duplicate listing detected.' });
      } else {
        setMessage({ type: 'error', text: detail || 'Error' });
      }
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleCharityReject = async (appId) => {
    setActionLoading(`reject-charity-${appId}`);
    try {
      await axiosInstance.put(`/admin/charity-application/${appId}/reject`, { adminNotes: charityAdminNotes });
      setMessage({ type: 'success', text: 'Application rejected' });
      setCharityConfirm(null);
      setCharityAdminNotes('');
      await fetchCharityApps();
      await fetchPendingCharityCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleCharityNeedsInfo = async (appId) => {
    setActionLoading(`info-charity-${appId}`);
    try {
      await axiosInstance.put(`/admin/charity-application/${appId}/needs-info`, { adminNotes: charityAdminNotes });
      setMessage({ type: 'success', text: 'Requested more information' });
      setCharityConfirm(null);
      setCharityAdminNotes('');
      await fetchCharityApps();
      await fetchPendingCharityCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleConvertListingType = async (businessId) => {
    setActionLoading(`convert-${businessId}`);
    try {
      const response = await axiosInstance.put(`/admin/business/${businessId}/convert-type`);
      setMessage({ type: 'success', text: response.data.message });
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const getOrgTypeLabel = (type) => {
    const labels = {
      'registered_charity': t('charity.orgTypeCharity'),
      'non_profit': t('charity.orgTypeNonProfit'),
      'community_group': t('charity.orgTypeCommunity'),
      'volunteer_organisation': t('charity.orgTypeVolunteer'),
    };
    return labels[type] || type;
  };

  const handleClaimApprove = async (claimId) => {
    setActionLoading(`approve-claim-${claimId}`);
    try {
      await axiosInstance.put(`/admin/claim/${claimId}/approve`, { adminNotes: claimAdminNotes });
      setMessage({ type: 'success', text: t('claimStatusApproved') });
      setClaimConfirm(null);
      setClaimAdminNotes('');
      await fetchClaims();
      await fetchPendingClaimsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleClaimReject = async (claimId) => {
    setActionLoading(`reject-claim-${claimId}`);
    try {
      await axiosInstance.put(`/admin/claim/${claimId}/reject`, { adminNotes: claimAdminNotes });
      setMessage({ type: 'success', text: t('claimStatusRejected') });
      setClaimConfirm(null);
      setClaimAdminNotes('');
      await fetchClaims();
      await fetchPendingClaimsCount();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleClaimNeedsInfo = async (claimId) => {
    setActionLoading(`info-claim-${claimId}`);
    try {
      await axiosInstance.put(`/admin/claim/${claimId}/needs-info`, {
        adminNotes: claimAdminNotes,
        infoRequests: claimInfoRequests,
      });
      setMessage({ type: 'success', text: t('claimNeedsInfoSent', 'Requested more information') });
      setClaimConfirm(null);
      setClaimAdminNotes('');
      setClaimInfoRequests([]);
      await fetchClaims();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeApprove = async () => {
    if (!confirmAction) return;
    setActionLoading(`approve-${confirmAction.businessId}`);
    try {
      await axiosInstance.put(`/admin/business/${confirmAction.businessId}/approve`);
      setMessage({ type: 'success', text: `"${confirmAction.businessName}" has been approved!` });
      setConfirmAction(null);
      await fetchStats();
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeReject = async () => {
    if (!confirmAction || !rejectReason.trim()) return;
    setActionLoading(`reject-${confirmAction.businessId}`);
    try {
      await axiosInstance.put(`/admin/business/${confirmAction.businessId}/reject`, { reason: rejectReason });
      setMessage({ type: 'success', text: `Business "${confirmAction.businessName}" has been rejected.` });
      setConfirmAction(null);
      setRejectReason('');
      await fetchStats();
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeDelete = async () => {
    if (!confirmAction) return;
    setActionLoading(`delete-${confirmAction.businessId}`);
    try {
      await axiosInstance.delete(`/admin/business/${confirmAction.businessId}`);
      setMessage({ type: 'success', text: `Business "${confirmAction.businessName}" has been deleted.` });
      setConfirmAction(null);
      await fetchStats();
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeSuspend = async () => {
    if (!confirmAction || !suspendReason.trim()) return;
    setActionLoading(`suspend-${confirmAction.businessId}`);
    try {
      await axiosInstance.put(`/admin/business/${confirmAction.businessId}/suspend`, { reason: suspendReason });
      setMessage({ type: 'success', text: `Business "${confirmAction.businessName}" has been suspended.` });
      setConfirmAction(null);
      setSuspendReason('');
      await fetchStats();
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // ============= TRIAL TEST MODE (Feb 2026) =============
  // These actions are only ever exposed for businesses owned by
  // TRIAL_TEST_MODE_EMAIL — the backend enforces the same restriction, the
  // UI just hides the buttons for other businesses.
  const executeTrialFastForward = async () => {
    if (!trialFastForwardBiz) return;
    setTrialFastForwardLoading(true);
    try {
      const minutes = Math.max(0, Math.min(parseInt(trialFastForwardMinutes, 10) || 0, 60 * 24));
      const res = await axiosInstance.post(
        `/admin/trial/fast-forward/${trialFastForwardBiz.id}`,
        { minutesFromNow: minutes, clearReminders: true }
      );
      setMessage({
        type: 'success',
        text: `Trial fast-forwarded → new trialEnd in ${minutes} min. ` +
          (res.data?.testModeEnabled
            ? 'TRIAL_TEST_MODE on — housekeeping will pick it up within 60 s.'
            : 'Enable TRIAL_TEST_MODE in .env for instant housekeeping, or click "Run housekeeping now".'),
      });
      setTrialFastForwardBiz(null);
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Fast-forward failed: ${error.response?.data?.detail || error.message}` });
    } finally {
      setTrialFastForwardLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
    }
  };

  const executeTrialRunHousekeepingNow = async () => {
    try {
      const res = await axiosInstance.post('/admin/trial/run-housekeeping-now');
      const c = res.data?.counters || {};
      setMessage({
        type: 'success',
        text: `Housekeeping run: scanned=${c.scanned || 0}, paused=${c.paused || 0}, reminders=${c.reminders_sent || 0}`,
      });
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Housekeeping failed: ${error.response?.data?.detail || error.message}` });
    } finally {
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
    }
  };

  const executeTrialResetTestBiz = async (business) => {
    if (!window.confirm(`Reset "${business.businessName}" back to a fresh 60-day trial?`)) return;
    try {
      await axiosInstance.post(`/admin/trial/reset-test-business/${business.id}`);
      setMessage({ type: 'success', text: `Reset — "${business.businessName}" is back on a fresh 60-day trial.` });
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Reset failed: ${error.response?.data?.detail || error.message}` });
    } finally {
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
    }
  };

  // Admin-hardening actions added Feb 2026 — hide/unhide, mark paid, mark refunded, restore
  const executeHide = async (business) => {
    setActionLoading(`hide-${business.id}`);
    try {
      const endpoint = business.isHidden ? 'unhide' : 'hide';
      await axiosInstance.put(`/admin/business/${business.id}/${endpoint}`);
      setMessage({ type: 'success', text: business.isHidden ? 'Business re-listed in public feed.' : 'Business hidden from public feed.' });
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeRestore = async (business) => {
    setActionLoading(`restore-${business.id}`);
    try {
      await axiosInstance.post(`/admin/business/${business.id}/restore`);
      setMessage({ type: 'success', text: 'Business restored.' });
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Modal state for the two reason-required actions (mark-paid / mark-refunded)
  const [reasonModal, setReasonModal] = useState(null); // { type, business, title, prompt }
  const [reasonInput, setReasonInput] = useState('');
  // Edit business modal state
  const [editModal, setEditModal] = useState(null); // business object or null
  const [editForm, setEditForm] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  useEffect(() => {
    axiosInstance.get('/geo/countries').then(r => setCountryOptions(r.data?.countries || [])).catch(() => {});
  }, []);
  const [amenityOptions, setAmenityOptions] = useState({});

  useEffect(() => {
    axiosInstance.get('/filters').then(r => setAmenityOptions(r.data || {})).catch(() => {});
  }, []);

  // Admin temporary closures (managed inside the edit-business modal)
  const [adminClosures, setAdminClosures] = useState([]);
  const [adminClosuresLoading, setAdminClosuresLoading] = useState(false);
  const [adminClosureSubmitting, setAdminClosureSubmitting] = useState(false);

  const fetchAdminClosures = async (businessId) => {
    setAdminClosuresLoading(true);
    try {
      const res = await axiosInstance.get(`/admin/business/${businessId}/closures`);
      setAdminClosures(res.data.closures || []);
    } catch { setAdminClosures([]); }
    finally { setAdminClosuresLoading(false); }
  };
  const createAdminClosure = async (payload) => {
    if (!editModal) return false;
    setAdminClosureSubmitting(true);
    try {
      await axiosInstance.post(`/admin/business/${editModal.id}/closures`, payload);
      await fetchAdminClosures(editModal.id);
      return true;
    } catch { return false; }
    finally { setAdminClosureSubmitting(false); }
  };
  const updateAdminClosure = async (closureId, payload) => {
    if (!editModal) return false;
    setAdminClosureSubmitting(true);
    try {
      await axiosInstance.put(`/admin/business/${editModal.id}/closures/${closureId}`, payload);
      await fetchAdminClosures(editModal.id);
      return true;
    } catch { return false; }
    finally { setAdminClosureSubmitting(false); }
  };
  const deleteAdminClosure = async (closureId) => {
    if (!editModal) return;
    try { await axiosInstance.delete(`/admin/business/${editModal.id}/closures/${closureId}`); await fetchAdminClosures(editModal.id); } catch {}
  };
  const reopenAdminClosure = async (closureId) => {
    if (!editModal) return;
    try { await axiosInstance.post(`/admin/business/${editModal.id}/closures/${closureId}/reopen`); await fetchAdminClosures(editModal.id); } catch {}
  };

  // Force-edit (power admin) modal
  const [forceEditModal, setForceEditModal] = useState(null); // business object or null
  const [forceEditForm, setForceEditForm] = useState({});

  // Hard-delete (permanent, typed confirmation) modal
  const [hardDeleteModal, setHardDeleteModal] = useState(null); // business object or null
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');

  const executeForceEdit = async () => {
    if (!forceEditModal) return;
    // Strip untouched fields and coerce plain types
    const payload = {};
    Object.keys(forceEditForm).forEach((k) => {
      const v = forceEditForm[k];
      if (v !== undefined && v !== (forceEditModal[k] ?? '')) payload[k] = v === '' ? null : v;
    });
    if (!Object.keys(payload).length) {
      setMessage({ type: 'error', text: 'No fields changed' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return;
    }
    setActionLoading(`force-edit-${forceEditModal.id}`);
    try {
      await axiosInstance.put(`/admin/business/${forceEditModal.id}/force-edit`, payload);
      setMessage({ type: 'success', text: `Force-edit applied to "${forceEditModal.businessName}".` });
      setForceEditModal(null);
      setForceEditForm({});
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeHardDelete = async () => {
    if (!hardDeleteModal) return;
    if (hardDeleteConfirmText.trim() !== hardDeleteModal.businessName) return;
    setActionLoading(`hard-delete-${hardDeleteModal.id}`);
    try {
      await axiosInstance.delete(
        `/admin/business/${hardDeleteModal.id}/hard-delete`,
        { params: { confirm: 'PERMANENT-DELETE' } }
      );
      setMessage({ type: 'success', text: `"${hardDeleteModal.businessName}" permanently deleted.` });
      setHardDeleteModal(null);
      setHardDeleteConfirmText('');
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeEdit = async () => {
    if (!editModal) return;
    setActionLoading(`edit-${editModal.id}`);
    try {
      const { instagram, facebook, tiktok, twitter, youtube, email, ...rest } = editForm;
      const payload = { ...rest, socialLinks: { instagram, facebook, tiktok, twitter, youtube } };
      if (email && email.trim()) payload.email = email.trim();
      await axiosInstance.put(`/admin/business/${editModal.id}/edit`, payload);
      setMessage({ type: 'success', text: `Business "${editModal.businessName}" updated.` });
      setEditModal(null);
      setEditForm({});
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeReasonAction = async () => {
    if (!reasonModal || !reasonInput.trim()) return;
    const { type, business } = reasonModal;
    setActionLoading(`${type}-${business.id}`);
    try {
      const endpoint = type === 'markPaid' ? 'mark-paid' : 'mark-refunded';
      await axiosInstance.put(`/admin/business/${business.id}/${endpoint}`, { reason: reasonInput.trim() });
      setMessage({
        type: 'success',
        text: type === 'markPaid'
          ? `Business "${business.businessName}" manually marked as paid.`
          : `Business "${business.businessName}" marked as refunded. Don't forget to refund via Stripe Dashboard.`,
      });
      setReasonModal(null);
      setReasonInput('');
      await fetchBusinesses();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Event actions
  const executeEventApprove = async () => {
    if (!eventConfirm) return;
    setActionLoading(`approve-event-${eventConfirm.eventId}`);
    try {
      await axiosInstance.put(`/admin/event/${eventConfirm.eventId}/approve`);
      setMessage({ type: 'success', text: `Event "${eventConfirm.eventTitle}" has been approved!` });
      setEventConfirm(null);
      await fetchEvents();
      await fetchAllEventsCount();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeEventReject = async () => {
    if (!eventConfirm || !eventRejectReason.trim()) return;
    setActionLoading(`reject-event-${eventConfirm.eventId}`);
    try {
      await axiosInstance.put(`/admin/event/${eventConfirm.eventId}/reject`, { reason: eventRejectReason });
      setMessage({ type: 'success', text: `Event "${eventConfirm.eventTitle}" has been rejected.` });
      setEventConfirm(null);
      setEventRejectReason('');
      await fetchEvents();
      await fetchAllEventsCount();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const executeEventDelete = async () => {
    if (!eventConfirm) return;
    setActionLoading(`delete-event-${eventConfirm.eventId}`);
    try {
      await axiosInstance.delete(`/admin/event/${eventConfirm.eventId}`);
      setMessage({ type: 'success', text: `Event "${eventConfirm.eventTitle}" deleted.` });
      setEventConfirm(null);
      await fetchEvents();
      await fetchAllEventsCount();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const duplicateEvent = async (event) => {
    setActionLoading(`duplicate-event-${event.id}`);
    try {
      const res = await axiosInstance.post(`/admin/event/${event.id}/duplicate`);
      setMessage({ type: 'success', text: `Event recreated. Edit dates on the new copy now.` });
      await fetchEvents();
      // Open edit modal for the new copy so admin can immediately set dates
      const newId = res.data?.id;
      if (newId) {
        const newDoc = (await axiosInstance.get('/admin/events', { params: { status: 'all' } })).data.items?.find(e => e.id === newId);
        if (newDoc) setEventEditModal({ ...newDoc });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const saveEventEdit = async () => {
    if (!eventEditModal) return;
    const payload = {
      title: eventEditModal.title,
      description: eventEditModal.description,
      startDate: eventEditModal.startDate,
      endDate: eventEditModal.endDate,
      location: eventEditModal.location,
      category: eventEditModal.category,
      price: eventEditModal.price,
    };
    setActionLoading(`edit-event-${eventEditModal.id}`);
    try {
      await axiosInstance.put(`/admin/event/${eventEditModal.id}`, payload);
      setMessage({ type: 'success', text: `Event "${eventEditModal.title}" updated.` });
      setEventEditModal(null);
      await fetchEvents();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3 mr-1" />, label: 'Pending' },
      approved: { bg: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3 mr-1" />, label: 'Approved' },
      suspended: { bg: 'bg-orange-100 text-orange-800', icon: <ShieldOff className="h-3 w-3 mr-1" />, label: 'Suspended' },
      rejected: { bg: 'bg-red-100 text-red-800', icon: <XCircle className="h-3 w-3 mr-1" />, label: 'Rejected' },
      expired: { bg: 'bg-gray-100 text-gray-800', icon: <Clock className="h-3 w-3 mr-1" />, label: 'Expired' },
      needs_more_info: { bg: 'bg-amber-100 text-amber-800', icon: <Clock className="h-3 w-3 mr-1" />, label: 'Needs Info' },
    };
    const badge = badges[status] || badges.rejected;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}>
        {badge.icon}{badge.label}
      </span>
    );
  };

  const pendingEventsCount = allEventsForBadge.length;

  // Page change helper — resets page to 1 when filters change
  const setPageFor = (tab, pg) => {
    setPagination(p => ({ ...p, [tab]: { ...p[tab], page: pg } }));
  };

  // Reusable pagination controls
  const PaginationControls = ({ tab }) => {
    const pg = pagination[tab];
    if (!pg || pg.totalPages <= 1) return null;
    const maxVisible = 5;
    let startPage = Math.max(1, pg.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pg.totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4" data-testid={`pagination-${tab}`}>
        <p className="text-sm text-gray-500">
          {t('showingOf', { from: ((pg.page - 1) * PAGE_SIZE) + 1, to: Math.min(pg.page * PAGE_SIZE, pg.total), total: pg.total })}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPageFor(tab, pg.page - 1)} disabled={pg.page <= 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`pagination-${tab}-prev`}>
            &laquo;
          </button>
          {pages.map(p => (
            <button key={p} onClick={() => setPageFor(tab, p)}
              className={`px-3 py-1.5 text-sm rounded-lg ${p === pg.page ? 'bg-purple-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}
              data-testid={`pagination-${tab}-page-${p}`}>
              {p}
            </button>
          ))}
          <button onClick={() => setPageFor(tab, pg.page + 1)} disabled={pg.page >= pg.totalPages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`pagination-${tab}-next`}>
            &raquo;
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-dashboard-title">
              {t('adminDashboard')}
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="view-site-button"
              >
                {t('viewSite')}
              </button>
              <button
                onClick={() => navigate('/admin/translations')}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                title="Translation coverage"
                data-testid="translations-link"
              >
                <Languages className="h-4 w-4" />
                i18n
              </button>
              <button
                onClick={() => navigate('/change-password')}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                title="Change admin password"
                data-testid="admin-change-password-link"
              >
                <Lock className="h-4 w-4" />
                <span className="hidden md:inline">{t('changePassword', 'Change Password')}</span>
              </button>
              <button
                onClick={() => navigate('/admin/stripe-rollout')}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors text-sm"
                title="Run the Iter 113 Stripe pricing rollout"
                data-testid="admin-stripe-rollout-link"
              >
                <CreditCard className="h-4 w-4" />
                <span className="hidden md:inline">Stripe Pricing</span>
              </button>
              <button
                onClick={() => navigate('/admin/safety-bulletin')}
                className="relative inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                title="Community Safety Hub moderation queue"
                data-testid="admin-safety-bulletin-link"
              >
                <ShieldAlert className="h-4 w-4" />
                <span className="hidden md:inline">Safety</span>
                {pendingSafetyCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid="admin-safety-pending-badge"
                  >
                    {pendingSafetyCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/admin/removal-requests')}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                title="Listing removal & edit-suggestion requests"
                data-testid="admin-removal-requests-link"
              >
                <ShieldAlert className="h-4 w-4" />
                <span className="hidden md:inline">Removals</span>
              </button>
              <button
                onClick={() => navigate('/admin/backup-recovery')}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                title="Backup & Recovery — full system backups and restore"
                data-testid="admin-backup-recovery-link"
              >
                <DatabaseBackup className="h-4 w-4" />
                <span className="hidden md:inline">Backup</span>
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                data-testid="admin-logout-button"
              >
                <LogOut className="h-4 w-4" />
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg text-lg font-medium ${
              message.type === 'error'
                ? 'bg-red-100 border-2 border-red-400 text-red-800'
                : 'bg-green-100 border-2 border-green-400 text-green-800'
            }`}
            data-testid="admin-message"
          >
            {message.text}
          </div>
        )}

        {/* SW Queue Health — early-warning of failed offline submissions */}
        {user?.token && <SwQueueHealthWidget token={user.token} />}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">{t('totalBusinesses')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalBusinesses}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">{t('pendingApproval')}</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pendingApprovals}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">{t('approved')}</p>
              <p className="text-3xl font-bold text-green-600">{stats.approvedBusinesses}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">{t('active')}</p>
              <p className="text-3xl font-bold text-blue-600">{stats.activeSubscriptions}</p>
            </div>
          </div>
        )}

        {/* Iter 150 — Admin "Possible Duplicates" review widget. Read-only. */}
        <PossibleDuplicatesWidget />

        {/* Iter 138 — Phase-1 anonymous-follow tracker. Renders only once
            there is at least one anonymous-with-favourites device, so it
            stays invisible on pre-launch envs. */}
        {followMetrics && (followMetrics.anonymousDevicesTotal > 0 || followMetrics.anonymousFollowsTotal > 0) && (
          <div
            className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg shadow-sm p-4 mb-8"
            data-testid="follow-metrics-card"
          >
            <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
              <div>
                <p className="text-sm font-semibold text-purple-900">
                  {t('adminFollowMetrics.title', 'Phase 1 — Anonymous Follow Adoption')}
                </p>
                <p className="text-xs text-purple-700 mt-0.5">
                  {t('adminFollowMetrics.subtitle', 'Tracks the email-optional onboarding path. Anonymous = device-only, no signup.')}
                </p>
              </div>
              <button
                onClick={fetchFollowMetrics}
                className="text-xs text-purple-700 underline hover:text-purple-900"
                data-testid="follow-metrics-refresh"
              >
                {t('refresh', 'Refresh')}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-md px-3 py-2.5" data-testid="follow-metrics-account-total">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('adminFollowMetrics.accountFollows', 'Account follows')}</p>
                <p className="text-2xl font-bold text-gray-900">{followMetrics.accountFollowsTotal}</p>
                <p className="text-[11px] text-gray-500">{t('adminFollowMetrics.allTime', 'all time')}</p>
              </div>
              <div className="bg-white rounded-md px-3 py-2.5" data-testid="follow-metrics-anon-total">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('adminFollowMetrics.anonymousFollows', 'Anonymous follows')}</p>
                <p className="text-2xl font-bold text-purple-700">{followMetrics.anonymousFollowsTotal}</p>
                <p className="text-[11px] text-gray-500">{t('adminFollowMetrics.allTime', 'all time')}</p>
              </div>
              <div className="bg-white rounded-md px-3 py-2.5" data-testid="follow-metrics-anon-today">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('adminFollowMetrics.anonymousToday', 'Anonymous today')}</p>
                <p className="text-2xl font-bold text-emerald-700">{followMetrics.anonymousFollowsToday}</p>
                <p className="text-[11px] text-gray-500">{t('adminFollowMetrics.utcDay', 'since 00:00 UTC')}</p>
              </div>
              <div className="bg-white rounded-md px-3 py-2.5" data-testid="follow-metrics-anon-devices">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('adminFollowMetrics.anonymousDevices', 'Anon. devices')}</p>
                <p className="text-2xl font-bold text-gray-900">{followMetrics.anonymousDevicesTotal}</p>
                <p className="text-[11px] text-gray-500">
                  {followMetrics.anonymousDevicesTotal > 0
                    ? `${Math.round((followMetrics.anonymousFollowsTotal / followMetrics.anonymousDevicesTotal) * 100)}% ${t('adminFollowMetrics.adoptionRate', 'follow at least one')}`
                    : t('adminFollowMetrics.adoptionRateZero', 'no devices yet')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Settings */}
        {trialTestStatus.businessIds.length > 0 && (
          <div className={`rounded-lg shadow p-3 mb-4 border ${trialTestStatus.enabled ? 'bg-fuchsia-50 border-fuchsia-300' : 'bg-gray-50 border-gray-200'}`} data-testid="trial-test-mode-banner">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm">
                <span className="font-semibold text-fuchsia-900">⚡ Trial Test Mode</span>
                <span className="text-fuchsia-800 ml-2">
                  {trialTestStatus.enabled
                    ? `ACTIVE — housekeeping every 60 s. ${trialTestStatus.businessIds.length} test business(es) loaded.`
                    : `Ready (set TRIAL_TEST_MODE=true in .env for 60 s loop). ${trialTestStatus.businessIds.length} test business(es) loaded.`}
                </span>
                <span className="block text-xs text-fuchsia-700 mt-1">
                  Test account: <strong>{trialTestStatus.testEmail}</strong>. Fast-forward buttons appear next to those businesses only.
                </span>
              </div>
              <button
                onClick={executeTrialRunHousekeepingNow}
                className="px-3 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg text-xs font-medium"
                data-testid="trial-housekeeping-now-banner-btn"
              >
                Run housekeeping now
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-8 flex items-center justify-between" data-testid="admin-config-panel">
          <div className="flex items-center gap-3">
            <Settings className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">Show Seeded Businesses in Public Feed</p>
              <p className="text-xs text-gray-500">When OFF, seeded listings only appear in search results</p>
            </div>
          </div>
          <button
            onClick={toggleShowSeeded}
            disabled={configLoading}
            className={`relative w-12 h-6 rounded-full transition-colors ${showSeededInFeed ? 'bg-purple-600' : 'bg-gray-300'} ${configLoading ? 'opacity-50' : ''}`}
            data-testid="toggle-show-seeded"
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showSeededInFeed ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Founding-Free migration — fix legacy "Payment required" businesses */}
        <div className="bg-white rounded-lg shadow mb-8 overflow-hidden border border-emerald-100">
          <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Activate Founding-Free for approved businesses</p>
                <p className="text-xs text-gray-500 max-w-xl">Moves every approved business stuck in the legacy "pending / no plan" state onto the free 12-month <strong>founders</strong> plan (full access, no payment required, never shows "Payment required"). Genuine active paid subscriptions are left untouched. Safe to run repeatedly.</p>
                {migratePreview !== null && (
                  <p className="text-xs font-medium text-emerald-700 mt-1" data-testid="migrate-preview">{migratePreview} business(es) pending migration.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => runFoundingFreeMigration(true)}
                disabled={migrateBusy}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="migrate-founding-free-preview"
              >
                Preview
              </button>
              <button
                onClick={() => { if (window.confirm('Move all eligible approved businesses to the free 12-month founders plan now?')) runFoundingFreeMigration(false); }}
                disabled={migrateBusy}
                className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="migrate-founding-free-run"
              >
                {migrateBusy ? 'Working…' : 'Run migration'}
              </button>
            </div>
          </div>
        </div>

        {/* Create Seeded Business */}
        <div className="bg-white rounded-lg shadow mb-8 overflow-hidden">
          <button
            onClick={() => setShowSeedForm(!showSeedForm)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            data-testid="toggle-seed-form"
          >
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-purple-600" />
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Create Unclaimed Business</p>
                <p className="text-xs text-gray-500">Add a seeded listing that can be claimed by business owners</p>
              </div>
            </div>
            <span className="text-gray-400 text-lg">{showSeedForm ? '−' : '+'}</span>
          </button>
          {showSeedForm && (
            <div className="p-4 border-t space-y-3" data-testid="seed-business-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" value={seedForm.businessName} onChange={e => setSeedForm(f => ({ ...f, businessName: e.target.value }))}
                  placeholder="Business Name *" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-name" />
                <select value={seedForm.category} onChange={e => setSeedForm(f => ({ ...f, category: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-category">
                  {['Restaurants','Cafes','Bars & Nightlife','Music Venues','Family Activities','Museums','Auto Repair','Mechanics','Plumbing','Tours','Hiking','Parks','Pharmacies','Holistic Health & Beauty','Translators','Services','Volunteer Work','Jobs','Community Help','Others'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input type="text" value={seedForm.city} onChange={e => setSeedForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="City" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-city" />
                <input type="text" value={seedForm.address} onChange={e => setSeedForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Address" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-address" />
                <input type="text" value={seedForm.phone} onChange={e => setSeedForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="Phone" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-phone" />
                <input type="email" value={seedForm.email} onChange={e => setSeedForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="seed-email" />
              </div>
              <textarea value={seedForm.description} onChange={e => setSeedForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" data-testid="seed-description" />
              <button onClick={handleCreateSeeded} disabled={seedSubmitting}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
                data-testid="seed-submit-btn">
                {seedSubmitting ? 'Creating...' : 'Create Seeded Business'}
              </button>
            </div>
          )}
        </div>

        {/* Main Tab Toggle: Businesses vs Events */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setActiveTab('businesses')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'businesses'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-businesses"
          >
            <Building2 className="h-5 w-5" />
            {t('businessManagement')}
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'events'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-events"
          >
            <Calendar className="h-5 w-5" />
            {t('eventManagement')}
            {pendingEventsCount > 0 && activeTab !== 'events' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {pendingEventsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('offers')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'offers'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-offers"
          >
            <Percent className="h-5 w-5" />
            Offers
          </button>
          <button
            onClick={() => setActiveTab('promotions')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'promotions'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-promotions"
          >
            <Tag className="h-5 w-5" />
            Price-2-Day
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'reviews'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-reviews"
          >
            <MessageSquare className="h-5 w-5" />
            {t('reviewModeration')}
            {pendingReviewsCount > 0 && activeTab !== 'reviews' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {pendingReviewsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'reports'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-reports"
          >
            <AlertTriangle className="h-5 w-5" />
            {t('reportManagement')}
            {openReportsCount > 0 && activeTab !== 'reports' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {openReportsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('claims')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'claims'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-claims"
          >
            <ShieldCheck className="h-5 w-5" />
            {t('claimManagement')}
            {pendingClaimsCount > 0 && activeTab !== 'claims' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {pendingClaimsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('charities')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'charities'
                ? 'bg-teal-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-charities"
          >
            <Heart className="h-5 w-5" />
            {t('charity.charityManagement')}
            {pendingCharityCount > 0 && activeTab !== 'charities' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {pendingCharityCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('outreach')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'outreach'
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-outreach"
          >
            <Users className="h-5 w-5" />
            Outreach CRM
          </button>
          <button
            onClick={() => setActiveTab('classifieds')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'classifieds'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-classifieds"
          >
            <ShieldCheck className="h-5 w-5" />
            Classifieds
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'invoices'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-invoices"
          >
            <FileText className="h-5 w-5" />
            Invoices
          </button>
          {flags.affiliatesEnabled && (
            <button
              onClick={() => setActiveTab('referrals')}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
                activeTab === 'referrals'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="tab-referrals"
            >
              <Users className="h-5 w-5" />
              Referrals
            </button>
          )}
          <button
            onClick={() => setActiveTab('issues')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'issues'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-issues"
          >
            <AlertTriangle className="h-5 w-5" />
            Issue Log
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'errors'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-errors"
          >
            <ShieldOff className="h-5 w-5" />
            Error Log
          </button>
          <button
            onClick={() => setActiveTab('regressions')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'regressions'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-regressions"
          >
            <History className="h-5 w-5" />
            Regression History
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors relative ${
              activeTab === 'users'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="tab-users"
          >
            <Users className="h-5 w-5" />
            Users
          </button>
        </div>

        {/* ============= BUSINESSES TAB ============= */}
        {activeTab === 'businesses' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200 flex justify-between items-center pr-4">
              <nav className="flex -mb-px">
                {['pending', 'approved', 'suspended', 'all', 'trash'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setPageFor('businesses', 1); }}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      filter === f
                        ? (f === 'trash' ? 'border-red-600 text-red-600' : 'border-purple-600 text-purple-600')
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`biz-filter-${f}`}
                  >
                    {f === 'pending' ? `${t('pendingTab')} (${stats?.pendingApprovals || 0})` :
                     f === 'approved' ? t('approved') :
                     f === 'suspended' ? t('suspended') :
                     f === 'trash' ? 'Trash' : t('all')}
                  </button>
                ))}
              </nav>
              <div className="flex items-center gap-2">
                <select
                  value={sourceFilter || 'all'}
                  onChange={e => { setSourceFilter(e.target.value === 'all' ? null : e.target.value); setPageFor('businesses', 1); }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="source-filter"
                >
                  <option value="all">{t('allSources')}</option>
                  <option value="seed">{t('seedOnly')}</option>
                  <option value="paid">{t('paidOnly')}</option>
                </select>
                <button
                  onClick={() => { fetchStats(); fetchBusinesses(); }}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Refresh"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    const src = sourceFilter ? `?source=${sourceFilter}` : '';
                    window.open(`${axiosInstance.defaults.baseURL}/admin/businesses/export${src}`, '_blank');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100"
                  data-testid="export-businesses-csv"
                  title="Export businesses as CSV"
                >
                  <FileText className="h-4 w-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Search + Filters */}
            <div className="px-6 pt-4 space-y-3">
              <div className="flex gap-2">
                <input
                  type="text" value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { setPageFor('businesses', 1); fetchBusinesses(); } }}
                  placeholder="Search by name, email, phone..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="admin-business-search"
                />
                <button onClick={() => { setPageFor('businesses', 1); fetchBusinesses(); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                  Search
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text" value={cityFilter}
                  onChange={e => setCityFilter(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { setPageFor('businesses', 1); } }}
                  placeholder="Filter by city..."
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none w-36"
                  data-testid="admin-city-filter"
                />
                <input
                  type="text" value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { setPageFor('businesses', 1); } }}
                  placeholder="Filter by category..."
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none w-40"
                  data-testid="admin-category-filter"
                />
                <select
                  value={contactFilter}
                  onChange={e => { setContactFilter(e.target.value); setPageFor('businesses', 1); }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="admin-contact-filter"
                >
                  <option value="">Contact: All</option>
                  <option value="has_phone">Has Phone</option>
                  <option value="missing_phone">Missing Phone</option>
                  <option value="has_email">Has Email</option>
                  <option value="has_website">Has Website</option>
                  <option value="missing_website">Missing Website</option>
                </select>
                <select
                  value={claimedFilter}
                  onChange={e => { setClaimedFilter(e.target.value); setPageFor('businesses', 1); }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="admin-claimed-filter"
                >
                  <option value="">Claimed: All</option>
                  <option value="claimed">Claimed</option>
                  <option value="unclaimed">Unclaimed</option>
                </select>
                {(cityFilter || categoryFilter || contactFilter || claimedFilter || searchTerm) && (
                  <button
                    onClick={() => { setCityFilter(''); setCategoryFilter(''); setContactFilter(''); setClaimedFilter(''); setSearchTerm(''); setPageFor('businesses', 1); }}
                    className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
                    data-testid="clear-admin-filters"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center py-8 text-gray-500">{t('loadingBusinesses')}</div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">{t('noBusinessesInCategory')}</div>
              ) : (
                <div className="space-y-4">
                  {businesses.map((business) => (
                    <div key={business.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">{business.businessName}</h3>
                            {getStatusBadge(business.approvalStatus)}
                            {business.source === 'seed' && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid={`seed-badge-${business.id}`}>
                                {t('seedListing')}
                              </span>
                            )}
                            {business.isClaimed === false && business.source === 'seed' && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                                {t('unclaimed')}
                              </span>
                            )}
                            {business.listingType === 'charity' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700 border border-teal-200" data-testid={`charity-listing-badge-${business.id}`}>
                                <Heart className="h-3 w-3" />
                                {t('charity.communityListing')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{business.category}</p>
                          {business.source === 'seed' && (
                            <p className="text-xs text-amber-600 mt-0.5">{t('seedPlan')}: {business.subscriptionPlan}</p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                        <div><strong>Email:</strong> <a href={`mailto:${business.email}`} className="text-purple-600 hover:underline">{business.email}</a></div>
                        <div><strong>Phone:</strong> {business.phone || 'N/A'}</div>
                        <div><strong>City:</strong> {business.city ? formatPlaceName(business.city) : 'N/A'}</div>
                        <div><strong>Address:</strong> {business.address || 'N/A'}</div>
                      </div>

                      {/* Feb 2026 — Linked owner account status (admin-only).
                          Two clearly-distinct states so admins never wonder why
                          recovery actions are missing on placeholder/seed rows. */}
                      {business.linkedOwner && (
                        business.linkedOwner.hasAccount ? (
                          <div
                            className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-3"
                            data-testid={`biz-linked-owner-${business.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Linked Owner Account</span>
                              {business.linkedOwner.deactivated && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded ring-1 ring-red-200">Deactivated</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-700 pl-6">
                              <span className="font-medium text-slate-500">Login email:</span>
                              <span className="font-mono text-slate-800">
                                {revealedOwnerIds.has(business.linkedOwner.userId)
                                  ? business.linkedOwner.email
                                  : business.linkedOwner.emailMasked}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleOwnerReveal(business.linkedOwner.userId)}
                                className="text-purple-700 hover:underline text-[10px]"
                                data-testid={`biz-owner-reveal-${business.id}`}
                              >
                                {revealedOwnerIds.has(business.linkedOwner.userId) ? 'Hide' : 'Reveal'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="p-3 bg-amber-50 border border-amber-300 rounded-lg mb-3"
                            data-testid={`biz-no-owner-${business.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                              <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">No Linked Owner Account</span>
                            </div>
                            <p className="text-xs text-amber-900 pl-6 leading-relaxed">
                              This listing has no registered user attached, so <strong>Send Password Reset</strong> and <strong>Change Login Email</strong> are not available. The business must first be claimed by the owner or manually linked to a user account before recovery actions can be used.
                            </p>
                          </div>
                        )
                      )}
                      {(business.description_en || business.description_es) && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          <strong>Description:</strong> {i18n.language.startsWith('es') ? business.description_es : business.description_en}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                        {business.approvalStatus === 'pending' && (
                          <>
                            <button
                              onClick={() => setConfirmAction({ type: 'approve', businessId: business.id, businessName: business.businessName })}
                              disabled={actionLoading === `approve-${business.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === `approve-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              {actionLoading === `approve-${business.id}` ? t('approving') : t('approve')}
                            </button>
                            <button
                              onClick={() => setConfirmAction({ type: 'reject', businessId: business.id, businessName: business.businessName })}
                              disabled={actionLoading === `reject-${business.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === `reject-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                              {actionLoading === `reject-${business.id}` ? t('rejecting') : t('reject')}
                            </button>
                          </>
                        )}
                        <a
                          href={`/admin/business/${business.id}`}
                          className="flex items-center gap-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          {t('view')}
                        </a>
                        {/* Iter 196 — Admin shortcut: copy the public listing URL
                            to clipboard without leaving the admin panel. */}
                        <button
                          type="button"
                          onClick={async () => {
                            const url = `${window.location.origin}/business/${business.id}`;
                            try { await navigator.clipboard.writeText(url); }
                            catch {
                              const ta = document.createElement('textarea');
                              ta.value = url; document.body.appendChild(ta); ta.select();
                              try { document.execCommand('copy'); } catch (_) { /* ignore */ }
                              document.body.removeChild(ta);
                            }
                            setMessage({ type: 'success', text: t('share.linkCopied', 'Business link copied to clipboard') });
                            setTimeout(() => setMessage({ type: '', text: '' }), 2500);
                          }}
                          className="flex items-center gap-1 px-3 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                          title={t('share.copyLink', 'Copy link')}
                          aria-label={t('share.copyLink', 'Copy link')}
                          data-testid={`admin-copy-business-link-${business.id}`}
                        >
                          <Copy className="h-4 w-4" />
                          <span className="hidden sm:inline">{t('share.copyLink', 'Copy link')}</span>
                        </button>

                        {/* Feb 2026 — Business-level recovery buttons */}
                        {business.linkedOwner?.hasAccount && (
                          <>
                            <button
                              type="button"
                              onClick={() => sendBusinessLoginReset(business)}
                              disabled={actionLoading === `biz-reset-${business.id}`}
                              className="flex items-center gap-1 px-3 py-2 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                              data-testid={`biz-send-reset-${business.id}`}
                              title="Email the owner account a password-reset link"
                            >
                              {actionLoading === `biz-reset-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                              <span className="hidden sm:inline">Send Reset</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setChangeEmailTarget({
                                id: business.linkedOwner.userId,
                                email: business.linkedOwner.email,
                                label: `Business · ${business.businessName}`,
                                businessId: business.id,
                              })}
                              disabled={!!actionLoading}
                              className="flex items-center gap-1 px-3 py-2 border border-indigo-300 text-indigo-700 text-sm rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
                              data-testid={`biz-change-email-${business.id}`}
                              title="Change the owner's registered login email (requires verification reason)"
                            >
                              <UserCog className="h-4 w-4" />
                              <span className="hidden sm:inline">Change Email</span>
                            </button>
                          </>
                        )}
                        {/* Iter 197 — QR code generator. For unclaimed listings
                            the modal shows the "Claim this listing" branded
                            poster variant. */}
                        <button
                          type="button"
                          onClick={() => setQrBusiness(business)}
                          className="flex items-center gap-1 px-3 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                          title={t('qr.button', 'QR code')}
                          aria-label={t('qr.button', 'QR code')}
                          data-testid={`admin-qr-business-${business.id}`}
                        >
                          <QrCode className="h-4 w-4" />
                          <span className="hidden sm:inline">{t('qr.button', 'QR code')}</span>
                        </button>
                        {business.approvalStatus === 'approved' && (
                          <button
                            onClick={() => setConfirmAction({ type: 'suspend', businessId: business.id, businessName: business.businessName })}
                            disabled={actionLoading === `suspend-${business.id}`}
                            className="flex items-center gap-1 px-4 py-2 border border-orange-300 text-orange-700 text-sm rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
                            data-testid={`suspend-btn-${business.id}`}
                          >
                            {actionLoading === `suspend-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                            {actionLoading === `suspend-${business.id}` ? t('suspending') : t('suspend')}
                          </button>
                        )}
                        {business.approvalStatus === 'approved' && (
                          <button
                            onClick={() => handleConvertListingType(business.id)}
                            disabled={actionLoading === `convert-${business.id}`}
                            className="flex items-center gap-1 px-4 py-2 border border-teal-300 text-teal-700 text-sm rounded-lg hover:bg-teal-50 transition-colors disabled:opacity-50"
                            data-testid={`convert-type-btn-${business.id}`}
                          >
                            {actionLoading === `convert-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
                            {business.listingType === 'charity' ? t('charity.convertToStandard') : t('charity.convertToCharity')}
                          </button>
                        )}
                        {/* Hardening Feb 2026: hide / mark-paid / mark-refunded / restore / edit */}
                        {!business.deletedAt && (
                          <button
                            onClick={() => { setEditModal(business); setAdminClosures([]); fetchAdminClosures(business.id); setEditForm({
                              businessName: business.businessName || '',
                              description: business.description || '',
                              category: business.category || '',
                              address: business.address || '',
                              city: business.city || '',
                              country: business.country || '',
                              countryCode: business.countryCode || '',
                              region: business.region || '',
                              province: business.province || '',
                              town: business.town || '',
                              localArea: business.localArea || '',
                              postcode: business.postcode || '',
                              phone: business.phone || '',
                              email: business.email || '',
                              website: business.website || '',
                              whatsapp: business.whatsapp || '',
                              openingHours: business.openingHours || '',
                              hours: business.hours || {},
                              specialHours: business.specialHours || '',
                              logoUrl: business.logoUrl || '',
                              instagram: (business.socialLinks || {}).instagram || '',
                              facebook: (business.socialLinks || {}).facebook || '',
                              tiktok: (business.socialLinks || {}).tiktok || '',
                              twitter: (business.socialLinks || {}).twitter || '',
                              youtube: (business.socialLinks || {}).youtube || '',
                              groupSizes: business.groupSizes || [],
                              dietaryOptions: business.dietaryOptions || [],
                              eventTypes: business.eventTypes || [],
                              venueType: business.venueType || [],
                              additionalFeatures: business.additionalFeatures || [],
                              accessibilityFeatures: business.accessibilityFeatures || [],
                            }); }}
                            className="flex items-center gap-1 px-3 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                            data-testid={`admin-edit-btn-${business.id}`}
                            title="Edit business profile (excludes Stripe/approval fields)"
                          >
                            Edit
                          </button>
                        )}
                        {/* Trial Test Mode — only shows for businesses owned by TRIAL_TEST_MODE_EMAIL */}
                        {trialTestStatus.businessIds.includes(business.id) && (business.subscriptionStatus === 'trial' || business.subscriptionStatus === 'paused') && (
                          <button
                            onClick={() => { setTrialFastForwardBiz(business); setTrialFastForwardMinutes(30); }}
                            className="flex items-center gap-1 px-3 py-2 border border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800 text-sm rounded-lg hover:bg-fuchsia-100 transition-colors font-medium"
                            data-testid={`trial-fast-forward-btn-${business.id}`}
                            title={`Fast-forward this TEST business's trial (restricted to ${trialTestStatus.testEmail})`}
                          >
                            ⚡ Fast-Forward Trial
                          </button>
                        )}
                        {trialTestStatus.businessIds.includes(business.id) && business.subscriptionStatus === 'paused' && (
                          <button
                            onClick={() => executeTrialResetTestBiz(business)}
                            className="flex items-center gap-1 px-3 py-2 border border-fuchsia-300 text-fuchsia-700 text-sm rounded-lg hover:bg-fuchsia-50 transition-colors"
                            data-testid={`trial-reset-btn-${business.id}`}
                            title="Reset this TEST business to a fresh 60-day trial"
                          >
                            Reset Trial
                          </button>
                        )}
                        {business.deletedAt ? (
                          <button
                            onClick={() => executeRestore(business)}
                            disabled={actionLoading === `restore-${business.id}`}
                            className="flex items-center gap-1 px-4 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-50"
                            data-testid={`admin-restore-btn-${business.id}`}
                          >
                            {actionLoading === `restore-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => executeHide(business)}
                              disabled={actionLoading === `hide-${business.id}`}
                              className={`flex items-center gap-1 px-3 py-2 border text-sm rounded-lg transition-colors disabled:opacity-50 ${business.isHidden ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              data-testid={`admin-hide-btn-${business.id}`}
                              title={business.isHidden ? 'Currently hidden — click to un-hide' : 'Hide this listing from public feed'}
                            >
                              {business.isHidden ? 'Unhide' : 'Hide'}
                            </button>
                            {!business.paymentConfirmed && (
                              <button
                                onClick={() => { setReasonModal({ type: 'markPaid', business }); setReasonInput(''); }}
                                className="flex items-center gap-1 px-3 py-2 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50 transition-colors"
                                data-testid={`admin-mark-paid-btn-${business.id}`}
                                title="Manually mark as paid (Stripe unchanged)"
                              >
                                Mark Paid
                              </button>
                            )}
                            {business.paymentConfirmed && !business.refunded && (
                              <button
                                onClick={() => { setReasonModal({ type: 'markRefunded', business }); setReasonInput(''); }}
                                className="flex items-center gap-1 px-3 py-2 border border-amber-300 text-amber-700 text-sm rounded-lg hover:bg-amber-50 transition-colors"
                                data-testid={`admin-mark-refunded-btn-${business.id}`}
                                title="Mark refunded (remember to process in Stripe too)"
                              >
                                Mark Refunded
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => setConfirmAction({ type: 'delete', businessId: business.id, businessName: business.businessName })}
                          disabled={actionLoading === `delete-${business.id}` || business.deletedAt}
                          className="flex items-center gap-1 px-4 py-2 border border-red-300 text-red-700 text-sm rounded-lg hover:bg-red-50 transition-colors ml-auto disabled:opacity-50"
                          data-testid={`admin-delete-btn-${business.id}`}
                        >
                          {actionLoading === `delete-${business.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          {actionLoading === `delete-${business.id}` ? t('deleting') : (business.deletedAt ? 'Deleted' : t('delete'))}
                        </button>
                        {/* Power-admin actions — force-edit ALL fields + hard-delete */}
                        {!business.deletedAt && (
                          <button
                            onClick={() => {
                              setForceEditModal(business);
                              setForceEditForm({
                                businessName: business.businessName || '',
                                email: business.email || '',
                                phone: business.phone || '',
                                website: business.website || '',
                                address: business.address || '',
                                city: business.city || '',
                                category: business.category || '',
                                description: business.description || '',
                                subscriptionStatus: business.subscriptionStatus || '',
                                subscriptionPlan: business.subscriptionPlan || '',
                                subscriptionEnd: business.subscriptionEnd || '',
                                paymentConfirmed: business.paymentConfirmed ? 'true' : 'false',
                                approvalStatus: business.approvalStatus || '',
                                refunded: business.refunded ? 'true' : 'false',
                                isClaimed: business.isClaimed ? 'true' : 'false',
                                source: business.source || '',
                              });
                            }}
                            className="flex items-center gap-1 px-3 py-2 border border-indigo-400 text-indigo-700 text-sm rounded-lg hover:bg-indigo-50 transition-colors"
                            data-testid={`admin-force-edit-btn-${business.id}`}
                            title="Power-edit ALL fields (including Stripe/approval). Audit-logged."
                          >
                            Force Edit
                          </button>
                        )}
                        <button
                          onClick={() => { setHardDeleteModal(business); setHardDeleteConfirmText(''); }}
                          className="flex items-center gap-1 px-3 py-2 border border-red-600 bg-red-50 text-red-800 text-sm rounded-lg hover:bg-red-100 transition-colors font-semibold"
                          data-testid={`admin-hard-delete-btn-${business.id}`}
                          title="PERMANENTLY delete this business and all associated records. Irreversible."
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Forever
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <PaginationControls tab="businesses" />
            </div>
          </div>
        )}

        {/* ============= EVENTS TAB ============= */}
        {activeTab === 'events' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200 flex justify-between items-center pr-4">
              <nav className="flex -mb-px">
                {['pending', 'approved', 'rejected', 'expired', 'all'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setEventFilter(f); setPageFor('events', 1); }}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      eventFilter === f
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`event-filter-${f}`}
                  >
                    {f === 'pending' ? t('pendingEvents') :
                     f === 'approved' ? t('approvedEvents') :
                     f === 'rejected' ? t('rejectedEvents') :
                     f === 'expired' ? t('expiredEvents') : t('allEvents')}
                  </button>
                ))}
              </nav>
              <button
                onClick={fetchEvents}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {eventsLoading ? (
                <div className="text-center py-8 text-gray-500">{t('loadingEvents')}</div>
              ) : events.length === 0 ? (
                <div className="text-center py-8 text-gray-500">{t('noEventsFound')}</div>
              ) : (
                <div className="space-y-4">
                  {events.map((event) => (
                    <div key={event.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`event-card-${event.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                            {getStatusBadge(event.status)}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {t('eventBy')} <span className="font-medium">{event.businessName}</span>
                          </p>
                        </div>
                      </div>

                      {event.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{event.description}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-3">
                        {event.location && (
                          <div><strong>{t('eventLocation')}:</strong> {formatPlaceName(event.location)}</div>
                        )}
                        <div>
                          <strong>{t('eventDates')}:</strong>{' '}
                          {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}
                        </div>
                        {event.category && (
                          <div><strong>Category:</strong> {event.category}</div>
                        )}
                      </div>

                      {event.rejectionReason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-sm">
                          <strong className="text-red-800">{t('rejectionReasonLabel')}:</strong>{' '}
                          <span className="text-red-700">{event.rejectionReason}</span>
                        </div>
                      )}

                      {/* Action buttons for pending events */}
                      {event.status === 'pending' && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => setEventConfirm({ type: 'approve', eventId: event.id, eventTitle: event.title })}
                            disabled={actionLoading === `approve-event-${event.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            data-testid={`approve-event-btn-${event.id}`}
                          >
                            {actionLoading === `approve-event-${event.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                            {t('approve')}
                          </button>
                          <button
                            onClick={() => setEventConfirm({ type: 'reject', eventId: event.id, eventTitle: event.title })}
                            disabled={actionLoading === `reject-event-${event.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            data-testid={`reject-event-btn-${event.id}`}
                          >
                            {actionLoading === `reject-event-${event.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                            {t('reject')}
                          </button>
                        </div>
                      )}

                      {/* Admin power actions — edit + duplicate + delete, always available */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => setEventEditModal({ ...event })}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                          data-testid={`admin-edit-event-btn-${event.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => duplicateEvent(event)}
                          disabled={actionLoading === `duplicate-event-${event.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                          data-testid={`admin-duplicate-event-btn-${event.id}`}
                          title="Create a new event from this one — pick new dates"
                        >
                          {actionLoading === `duplicate-event-${event.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
                          Recreate
                        </button>
                        <button
                          onClick={() => setEventConfirm({ type: 'delete', eventId: event.id, eventTitle: event.title })}
                          disabled={actionLoading === `delete-event-${event.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                          data-testid={`admin-delete-event-btn-${event.id}`}
                        >
                          {actionLoading === `delete-event-${event.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <PaginationControls tab="events" />
            </div>
          </div>
        )}

        {activeTab === 'offers' && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <AdminOffersTab />
          </div>
        )}

        {activeTab === 'promotions' && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <AdminPricePromotions />
          </div>
        )}
      </div>

      {/* ============= REVIEWS TAB ============= */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200 flex justify-between items-center pr-4">
              <nav className="flex -mb-px">
                {['pending', 'approved', 'rejected', 'all'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setReviewFilter(f); setPageFor('reviews', 1); }}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      reviewFilter === f
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`review-filter-${f}`}
                  >
                    {f === 'pending' ? t('pendingReviews') :
                     f === 'approved' ? t('approvedReviews') :
                     f === 'rejected' ? t('rejectedReviews') : t('allReviews')}
                  </button>
                ))}
              </nav>
              <button onClick={fetchReviews} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {reviewsLoading ? (
                <div className="text-center py-8 text-gray-500">{t('loadingEvents')}</div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-8 text-gray-500">{t('noReviewsFound')}</div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`admin-review-${review.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`h-4 w-4 ${review.rating >= s ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                              ))}
                            </div>
                            {getStatusBadge(review.status)}
                          </div>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">{review.userName}</span> {t('reviewFor')} <span className="font-medium">{review.businessName || review.businessId?.slice(0, 8)}</span>
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">{review.comment}</p>
                      {review.status === 'pending' && (
                        <div className="flex gap-2 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => handleReviewApprove(review.id)}
                            disabled={actionLoading === `approve-review-${review.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            data-testid={`approve-review-btn-${review.id}`}
                          >
                            {actionLoading === `approve-review-${review.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                            {t('approveReview')}
                          </button>
                          <button
                            onClick={() => handleReviewReject(review.id)}
                            disabled={actionLoading === `reject-review-${review.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            data-testid={`reject-review-btn-${review.id}`}
                          >
                            {actionLoading === `reject-review-${review.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                            {t('rejectReview')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <PaginationControls tab="reviews" />
            </div>
          </div>
        )}

      {/* ============= REPORTS TAB ============= */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="flex -mb-px">
                  {['open', 'resolved', 'dismissed', 'all'].map((f) => (
                    <button
                      key={f}
                      onClick={() => { setReportStatusFilter(f); setPageFor('reports', 1); }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        reportStatusFilter === f
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid={`report-status-filter-${f}`}
                    >
                      {f === 'open' ? t('openReports') :
                       f === 'resolved' ? t('resolvedReports') :
                       f === 'dismissed' ? t('dismissedReports') : t('allReports')}
                    </button>
                  ))}
                </div>
                <select
                  value={reportTypeFilter}
                  onChange={e => { setReportTypeFilter(e.target.value); setPageFor('reports', 1); }}
                  className="ml-auto text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="report-type-filter"
                >
                  <option value="all">{t('allTypes')}</option>
                  <option value="business">{t('reportTypeBusiness')}</option>
                  <option value="event">{t('reportTypeEvent')}</option>
                  <option value="review">{t('reportTypeReview')}</option>
                </select>
                <button onClick={fetchReports} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                  <RefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {reportsLoading ? (
                <div className="text-center py-8 text-gray-500">{t('loadingReports')}</div>
              ) : reports.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="no-reports">{t('noReportsFound')}</div>
              ) : (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`admin-report-${report.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              report.targetType === 'business' ? 'bg-blue-100 text-blue-700' :
                              report.targetType === 'event' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`} data-testid={`report-type-badge-${report.id}`}>
                              {report.targetType === 'business' ? <Building2 className="h-3 w-3" /> :
                               report.targetType === 'event' ? <Calendar className="h-3 w-3" /> :
                               <MessageSquare className="h-3 w-3" />}
                              {t(`reportType_${report.targetType}`)}
                            </span>
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              report.status === 'open' ? 'bg-orange-100 text-orange-700' :
                              report.status === 'resolved' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`} data-testid={`report-status-badge-${report.id}`}>
                              {t(`reportStatus_${report.status}`)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900">{report.targetName}</p>
                          <p className="text-xs text-gray-500">{t('reportedBy')}: {report.reporterName}</p>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(report.createdAt).toLocaleDateString()}</span>
                      </div>

                      <div className="mb-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('reason')}:</span>
                        <span className="ml-1 text-sm text-gray-700">{getReportReasonLabel(report.reason)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded-lg">{report.details}</p>

                      {/* Admin Notes */}
                      {(report.adminNotes || editingNotes === report.id) && (
                        <div className="mb-3">
                          <span className="text-xs font-medium text-gray-500 uppercase">{t('adminNotes')}:</span>
                          {editingNotes === report.id ? (
                            <div className="mt-1">
                              <textarea
                                value={notesText}
                                onChange={e => setNotesText(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                                rows={2}
                                data-testid={`report-notes-input-${report.id}`}
                              />
                              <div className="flex gap-2 mt-1">
                                <button
                                  onClick={() => handleSaveNotes(report.id)}
                                  disabled={actionLoading === `notes-report-${report.id}`}
                                  className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                  data-testid={`save-notes-btn-${report.id}`}
                                >
                                  {actionLoading === `notes-report-${report.id}` ? <RefreshCw className="h-3 w-3 animate-spin inline" /> : t('saveNotes')}
                                </button>
                                <button onClick={() => { setEditingNotes(null); setNotesText(''); }} className="px-3 py-1 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                                  {t('cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mt-1 italic">{report.adminNotes}</p>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 pt-3 border-t border-gray-200">
                        {report.status === 'open' && (
                          <>
                            <button
                              onClick={() => handleReportResolve(report.id)}
                              disabled={actionLoading === `resolve-report-${report.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              data-testid={`resolve-report-btn-${report.id}`}
                            >
                              {actionLoading === `resolve-report-${report.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              {t('resolveReport')}
                            </button>
                            <button
                              onClick={() => handleReportDismiss(report.id)}
                              disabled={actionLoading === `dismiss-report-${report.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                              data-testid={`dismiss-report-btn-${report.id}`}
                            >
                              {actionLoading === `dismiss-report-${report.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                              {t('dismissReport')}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { setEditingNotes(report.id); setNotesText(report.adminNotes || ''); }}
                          className="flex items-center gap-1 px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors ml-auto"
                          data-testid={`edit-notes-btn-${report.id}`}
                        >
                          <FileText className="h-4 w-4" />
                          {t('editNotes')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <PaginationControls tab="reports" />
            </div>
          </div>
        )}

      {/* ============= CLAIMS TAB ============= */}
      {activeTab === 'claims' && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['pending', 'needs_more_info', 'approved', 'rejected', 'all'].map((f) => (
                <button
                  key={f}
                  onClick={() => { setClaimFilter(f); setPageFor('claims', 1); }}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    claimFilter === f
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`claim-filter-${f}`}
                >
                  {f === 'pending' && t('pendingClaims')}
                  {f === 'needs_more_info' && t('claimNeedsInfo', 'Needs Info')}
                  {f === 'approved' && t('approvedClaims')}
                  {f === 'rejected' && t('rejectedClaims')}
                  {f === 'all' && t('allClaims')}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {claimsLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto text-purple-600" />
                <p className="mt-2 text-gray-500">{t('loadingClaims')}</p>
              </div>
            ) : claims.length === 0 ? (
              <div className="text-center py-8">
                <ShieldCheck className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">{t('noClaims')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {claims.map((claim) => (
                  <div key={claim.id} className="border border-gray-200 rounded-lg p-5" data-testid={`claim-card-${claim.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{claim.businessName}</h3>
                        <p className="text-sm text-gray-500">ID: {claim.businessId}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {claim.autoApproved && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                            <ShieldCheck className="h-3 w-3" />
                            {t('claimAutoApprovedBadge')}
                          </span>
                        )}
                        {claim.emailVerified && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full" data-testid={`claim-email-verified-badge-${claim.id}`}>
                            <CheckCircle className="h-3 w-3" />
                            {t('claimEmailVerifiedBadge')}
                          </span>
                        )}
                        {claim.emailMatch && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            <Mail className="h-3 w-3" />
                            {t('claimEmailMatchBadge')}
                          </span>
                        )}
                        {claim.phoneMatch && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full" data-testid={`claim-phone-match-badge-${claim.id}`}>
                            <Phone className="h-3 w-3" />
                            {t('claimPhoneMatchBadge')}
                          </span>
                        )}
                        {getStatusBadge(claim.status)}
                      </div>
                    </div>

                    {/* Claimant vs Listing comparison */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-sm">
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">{t('claimantDetails')}</p>
                        <p><span className="text-gray-500">{t('claimantName')}:</span> <span className="font-medium">{claim.claimantName}</span></p>
                        <p><span className="text-gray-500">{t('claimantEmail')}:</span> <span className="font-medium">{claim.claimantEmail}</span></p>
                        <p><span className="text-gray-500">{t('claimPhone')}:</span> <span className="font-medium">{claim.claimantPhone || '—'}</span></p>
                        <p><span className="text-gray-500">{t('claimRole')}:</span> <span className="font-medium capitalize">{claim.claimantRole || '—'}</span></p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('listingDetails')}</p>
                        <p><span className="text-gray-500">{t('claimPhone')}:</span> <span className={`font-medium ${claim.phoneMatch ? 'text-green-700' : ''}`}>{claim.listingPhone || '—'}</span></p>
                        <p><span className="text-gray-500">{t('claimantEmail')}:</span> <span className="font-medium">{claim.listingEmail || '—'}</span></p>
                        <p><span className="text-gray-500">{t('date')}:</span> <span className="font-medium">{new Date(claim.createdAt).toLocaleDateString()}</span></p>
                      </div>
                    </div>

                    {/* Duplicate claim warning */}
                    {claim.duplicateCount > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid={`duplicate-warning-${claim.id}`}>
                        <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                          <AlertTriangle className="h-4 w-4" />
                          This contact has {claim.duplicateCount} other claim(s)
                        </p>
                        <div className="mt-2 space-y-1">
                          {claim.duplicateClaims.map((dc) => (
                            <p key={dc.id} className="text-xs text-amber-700">
                              <span className="font-medium">{dc.businessName}</span>
                              {' — '}
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                dc.status === 'approved' ? 'bg-green-100 text-green-700' :
                                dc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>{dc.status}</span>
                              {dc.claimantEmail && ` (${dc.claimantEmail})`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Proof documents */}
                    {claim.proofDocUrls && claim.proofDocUrls.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">{t('claimProofDocs')}</p>
                        <div className="flex flex-wrap gap-2">
                          {claim.proofDocUrls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-purple-600 hover:bg-purple-50 transition-colors"
                              data-testid={`claim-proof-link-${claim.id}-${i}`}
                            >
                              <FileText className="h-3.5 w-3.5" />
                              {t('claimProofDoc')} {i + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {claim.message && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">{t('claimMessage_label')}:</span>
                        <p className="text-sm text-gray-700 mt-1">{claim.message}</p>
                      </div>
                    )}

                    {claim.adminNotes && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <span className="text-sm text-yellow-700 font-medium">{t('adminNotes')}:</span>
                        <p className="text-sm text-yellow-800 mt-1">{claim.adminNotes}</p>
                      </div>
                    )}

                    {/* Previous info requests */}
                    {claim.infoRequestHistory && claim.infoRequestHistory.length > 0 && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`info-history-${claim.id}`}>
                        <span className="text-xs font-semibold text-amber-700 uppercase">Previous Info Requests:</span>
                        {claim.infoRequestHistory.map((req, ri) => (
                          <div key={ri} className="mt-2 text-xs text-amber-800">
                            <p className="text-[10px] text-amber-500">{new Date(req.requestedAt).toLocaleDateString()}</p>
                            {req.infoRequests?.length > 0 && (
                              <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
                                {req.infoRequests.map((item, ii) => <li key={ii}>{item}</li>)}
                              </ul>
                            )}
                            {req.customNote && <p className="mt-0.5 italic">{req.customNote}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {(claim.status === 'pending' || claim.status === 'needs_more_info') && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => { setClaimConfirm({ id: claim.id, type: 'approve', businessName: claim.businessName, claimantName: claim.claimantName }); setClaimAdminNotes(''); }}
                          className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                          data-testid={`approve-claim-btn-${claim.id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                          {t('approveClaim')}
                        </button>
                        <button
                          onClick={() => { setClaimConfirm({ id: claim.id, type: 'reject', businessName: claim.businessName, claimantName: claim.claimantName }); setClaimAdminNotes(''); }}
                          className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                          data-testid={`reject-claim-btn-${claim.id}`}
                        >
                          <XCircle className="h-4 w-4" />
                          {t('rejectClaim')}
                        </button>
                        {claim.status !== 'needs_more_info' && (
                          <button
                            onClick={() => { setClaimConfirm({ id: claim.id, type: 'needs_info', businessName: claim.businessName, claimantName: claim.claimantName }); setClaimAdminNotes(''); setClaimInfoRequests([]); }}
                            className="flex items-center gap-1 px-4 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors"
                            data-testid={`needs-info-claim-btn-${claim.id}`}
                          >
                            {t('claimNeedsInfo', 'Needs Info')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <PaginationControls tab="claims" />
          </div>
        </div>
      )}

      {/* ============= CLAIM CONFIRMATION MODAL ============= */}
      {claimConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {claimConfirm.type === 'approve' ? t('approveClaim') : claimConfirm.type === 'needs_info' ? t('claimNeedsInfo', 'Request More Info') : t('rejectClaim')}
                </h3>
                <button onClick={() => { setClaimConfirm(null); setClaimAdminNotes(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  {claimConfirm.type === 'approve' ? t('approveClaimDesc') : claimConfirm.type === 'needs_info' ? t('claimNeedsInfoDesc', 'Select what information is needed from the claimant.') : t('rejectClaimDesc')}
                </p>
                <p className="font-semibold text-gray-900">{t('claimFor')}: "{claimConfirm.businessName}"</p>
                <p className="text-sm text-gray-600">{t('claimantName')}: {claimConfirm.claimantName}</p>
              </div>

              {/* Structured info request checkboxes */}
              {claimConfirm.type === 'needs_info' && (
                <div className="mb-4 space-y-2" data-testid="info-request-checkboxes">
                  {[
                    'Please provide proof of business ownership',
                    'Please provide a business registration document',
                    'Please provide proof of relationship to this business',
                    'Please confirm the business phone number',
                    'Please confirm the business email address',
                    'Please provide a website or social media page linked to the business',
                  ].map((item) => (
                    <label key={item} className="flex items-start gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-amber-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={claimInfoRequests.includes(item)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setClaimInfoRequests(prev => [...prev, item]);
                          } else {
                            setClaimInfoRequests(prev => prev.filter(r => r !== item));
                          }
                        }}
                        className="mt-0.5 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        data-testid={`info-req-${item.substring(7, 20).toLowerCase().replace(/[^a-z]/g, '-')}`}
                      />
                      <span className="text-sm text-gray-700">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              <textarea
                value={claimAdminNotes}
                onChange={(e) => setClaimAdminNotes(e.target.value)}
                placeholder={claimConfirm.type === 'needs_info' ? 'Additional custom note (optional)...' : t('adminNotes') + '...'}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none mb-4"
                rows={claimConfirm.type === 'needs_info' ? 2 : 3}
                data-testid="claim-admin-notes-input"
              />
              <div className="flex gap-3">
                <button onClick={() => { setClaimConfirm(null); setClaimAdminNotes(''); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  {t('cancel')}
                </button>
                {claimConfirm.type === 'approve' && (
                  <button
                    onClick={() => handleClaimApprove(claimConfirm.id)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-approve-claim-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    {t('approveClaim')}
                  </button>
                )}
                {claimConfirm.type === 'reject' && (
                  <button
                    onClick={() => handleClaimReject(claimConfirm.id)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-reject-claim-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    {t('rejectClaim')}
                  </button>
                )}
                {claimConfirm.type === 'needs_info' && (
                  <button
                    onClick={() => handleClaimNeedsInfo(claimConfirm.id)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-needs-info-claim-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
                    {t('claimNeedsInfo', 'Request Info')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= BUSINESS CONFIRMATION MODAL ============= */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {confirmAction.type === 'approve' && t('approveBusiness')}
                  {confirmAction.type === 'reject' && t('rejectBusiness')}
                  {confirmAction.type === 'delete' && t('deleteBusiness')}
                  {confirmAction.type === 'suspend' && t('suspendBusiness')}
                </h3>
                <button onClick={() => { setConfirmAction(null); setRejectReason(''); setSuspendReason(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-600 mb-2">
                  {confirmAction.type === 'approve' && t('approveDesc')}
                  {confirmAction.type === 'reject' && t('rejectDesc')}
                  {confirmAction.type === 'delete' && t('deleteDesc')}
                  {confirmAction.type === 'suspend' && t('suspendDesc')}
                </p>
                <p className="font-semibold text-lg text-gray-900">"{confirmAction.businessName}"</p>
                {confirmAction.type === 'reject' && (
                  <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('enterRejectReason')} className="w-full mt-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none" rows={3} />
                )}
                {confirmAction.type === 'suspend' && (
                  <textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder={t('enterSuspendReason')} className="w-full mt-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none resize-none" rows={3} data-testid="suspend-reason-input" />
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setConfirmAction(null); setRejectReason(''); setSuspendReason(''); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  {t('cancel')}
                </button>
                {confirmAction.type === 'approve' && (
                  <button onClick={executeApprove} disabled={actionLoading} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    {actionLoading ? t('approving') : t('yesApprove')}
                  </button>
                )}
                {confirmAction.type === 'reject' && (
                  <button onClick={executeReject} disabled={actionLoading || !rejectReason.trim()} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    {actionLoading ? t('rejecting') : t('rejectBusiness')}
                  </button>
                )}
                {confirmAction.type === 'delete' && (
                  <button onClick={executeDelete} disabled={actionLoading} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {actionLoading ? t('deleting') : t('yesDelete')}
                  </button>
                )}
                {confirmAction.type === 'suspend' && (
                  <button onClick={executeSuspend} disabled={actionLoading || !suspendReason.trim()} className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-suspend-btn">
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                    {actionLoading ? t('suspending') : t('yesSuspend')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= REASON-REQUIRED ACTION MODAL (Mark Paid / Mark Refunded — Feb 2026) ============= */}
      {reasonModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reason-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {reasonModal.type === 'markPaid' ? 'Mark as Paid (Manual Override)' : 'Mark as Refunded'}
                </h3>
                <button onClick={() => { setReasonModal(null); setReasonInput(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-5">
                <p className="font-semibold text-gray-900 mb-2">"{reasonModal.business.businessName}"</p>
                <p className="text-sm text-gray-600 mb-4">
                  {reasonModal.type === 'markPaid'
                    ? 'Stripe will NOT be charged or updated — this is a local-only override for manual or offline payments (e.g. bank transfer). A reason is required for the audit trail.'
                    : 'Marks this business as refunded locally and voids any affiliate commission. You must still process the actual refund in Stripe Dashboard. A reason is required for the audit trail.'}
                </p>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason (required)</label>
                <textarea
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  placeholder={reasonModal.type === 'markPaid' ? 'e.g. Bank transfer received, ref #12345' : 'e.g. Customer requested refund due to service issue'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none text-sm"
                  rows={3}
                  maxLength={500}
                  data-testid="reason-modal-input"
                  autoFocus
                />
                <p className="text-[11px] text-gray-400 mt-1">{reasonInput.length}/500</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setReasonModal(null); setReasonInput(''); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={executeReasonAction}
                  disabled={!reasonInput.trim() || !!actionLoading}
                  className={`flex-1 px-4 py-3 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2 ${reasonModal.type === 'markPaid' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                  data-testid="reason-modal-confirm"
                >
                  {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  {reasonModal.type === 'markPaid' ? 'Confirm Mark Paid' : 'Confirm Mark Refunded'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= EDIT BUSINESS MODAL (Admin — Feb 2026) ============= */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-business-modal">
          <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Edit Business — {editModal.businessName}</h3>
                <button onClick={() => { setEditModal(null); setEditForm({}); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">Stripe IDs, subscription and approval status are not editable here — use the dedicated buttons for those.</p>
              <div className="space-y-3">
                {[
                  { field: 'businessName', label: 'Business Name' },
                  { field: 'category', label: 'Category' },
                  { field: 'city', label: 'City' },
                  { field: 'address', label: 'Address' },
                  { field: 'phone', label: 'Phone' },
                  { field: 'email', label: 'Contact Email' },
                  { field: 'website', label: 'Website' },
                  { field: 'whatsapp', label: 'WhatsApp' },
                  { field: 'logoUrl', label: 'Logo URL' },
                  { field: 'instagram', label: 'Instagram URL' },
                  { field: 'facebook', label: 'Facebook URL' },
                  { field: 'tiktok', label: 'TikTok URL' },
                  { field: 'twitter', label: 'X (Twitter) URL' },
                  { field: 'youtube', label: 'YouTube URL' },
                ].map(({ field, label }) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                    <input
                      type="text" value={editForm[field] || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                      data-testid={`edit-field-${field}`}
                    />
                  </div>
                ))}

                {/* Stage 2B — worldwide structured location (lat/lng auto-geocoded on save) */}
                <div className="sm:col-span-2 mt-2 pt-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Worldwide location</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                      <select
                        value={editForm.country || ''}
                        onChange={(e) => setEditForm(f => ({ ...f, country: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                        data-testid="edit-field-country"
                      >
                        <option value="">— Select country —</option>
                        {countryOptions.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Region / State</label>
                      <input type="text" value={editForm.region || ''} onChange={(e) => setEditForm(f => ({ ...f, region: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-field-region" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Province</label>
                      <input type="text" value={editForm.province || ''} onChange={(e) => setEditForm(f => ({ ...f, province: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-field-province" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Town / City</label>
                      <input type="text" value={editForm.town || ''} onChange={(e) => setEditForm(f => ({ ...f, town: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-field-town" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Local Area</label>
                      <input type="text" value={editForm.localArea || ''} onChange={(e) => setEditForm(f => ({ ...f, localArea: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-field-localArea" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                      <input type="text" value={editForm.postcode || ''} onChange={(e) => setEditForm(f => ({ ...f, postcode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-field-postcode" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Coordinates are generated automatically from this location on save (country-safe, with coarse fallback). Legacy City stays in sync with Town/City.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={3} value={editForm.description || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                    data-testid="edit-field-description"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Special / holiday opening hours</label>
                  <textarea
                    rows={2} value={editForm.specialHours || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, specialHours: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                    data-testid="edit-field-specialHours"
                  />
                </div>

                {/* Structured weekly opening hours — same editor owners use */}
                <div data-testid="admin-opening-hours">
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Opening days &amp; times</label>
                  <WeeklyHoursEditor hours={editForm.hours} onChange={(h) => setEditForm(f => ({ ...f, hours: h }))} testIdPrefix="admin-hours" />
                  <p className="text-xs text-gray-400 mt-1">Tick "Closed" or leave a day blank if the business does not open. Updates the public listing &amp; Open-Now.</p>
                </div>

                {/* Features & Amenities (same options as the owner editor) */}
                {[
                  { field: 'groupSizes', label: 'Group size accommodation', opts: amenityOptions.groupSizes },
                  { field: 'dietaryOptions', label: 'Dietary options', opts: amenityOptions.dietaryOptions },
                  { field: 'eventTypes', label: 'Event types', opts: amenityOptions.eventTypes },
                  { field: 'venueType', label: 'Venue type', opts: amenityOptions.venueTypes },
                  { field: 'additionalFeatures', label: 'Additional features', opts: amenityOptions.additionalFeatures },
                  { field: 'accessibilityFeatures', label: 'Accessibility / facilities', opts: amenityOptions.accessibilityFeatures },
                ].map(({ field, label, opts }) => (
                  <div key={field} data-testid={`edit-amenity-group-${field}`}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(opts || []).map((opt) => {
                        const selected = (editForm[field] || []).includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setEditForm(f => {
                              const cur = f[field] || [];
                              return { ...f, [field]: selected ? cur.filter(x => x !== opt) : [...cur, opt] };
                            })}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            data-testid={`edit-amenity-${field}-${opt}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-200" data-testid="admin-closure-wrapper">
                <ClosureSection
                  closures={adminClosures}
                  loading={adminClosuresLoading}
                  submitting={adminClosureSubmitting}
                  onCreate={createAdminClosure}
                  onUpdate={updateAdminClosure}
                  onDelete={deleteAdminClosure}
                  onReopen={reopenAdminClosure}
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setEditModal(null); setEditForm({}); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={executeEdit}
                  disabled={!!actionLoading}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="edit-save-btn"
                >
                  {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= POWER ADMIN — FORCE EDIT MODAL ============= */}
      {forceEditModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="force-edit-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-gray-900">Force Edit — {forceEditModal.businessName}</h3>
                <button onClick={() => { setForceEditModal(null); setForceEditForm({}); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <strong>Warning:</strong> these fields bypass the normal guardrails. Stripe IDs, subscription state and approval flags are all writable here. Every change is audit-logged to the business edit history.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { field: 'businessName', label: 'Business Name' },
                  { field: 'email', label: 'Contact Email' },
                  { field: 'phone', label: 'Phone' },
                  { field: 'website', label: 'Website' },
                  { field: 'address', label: 'Address' },
                  { field: 'city', label: 'City' },
                  { field: 'category', label: 'Category' },
                  { field: 'source', label: 'Source (seed/paid/...)' },
                  { field: 'subscriptionPlan', label: 'Subscription Plan (basic/6months/12months/seed)' },
                  { field: 'subscriptionStatus', label: 'Subscription Status (pending/active/cancelled/expired)' },
                  { field: 'subscriptionEnd', label: 'Subscription End (ISO date)' },
                  { field: 'approvalStatus', label: 'Approval Status (pending/approved/rejected/suspended)' },
                ].map(({ field, label }) => (
                  <div key={field} className={field === 'businessName' || field === 'email' ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                    <input
                      type="text" value={forceEditForm[field] ?? ''}
                      onChange={(e) => setForceEditForm(f => ({ ...f, [field]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid={`force-edit-field-${field}`}
                    />
                  </div>
                ))}
                {[
                  { field: 'paymentConfirmed', label: 'Payment Confirmed' },
                  { field: 'refunded', label: 'Refunded' },
                  { field: 'isClaimed', label: 'Is Claimed' },
                ].map(({ field, label }) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                    <select
                      value={forceEditForm[field] ?? 'false'}
                      onChange={(e) => setForceEditForm(f => ({ ...f, [field]: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid={`force-edit-bool-${field}`}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={3} value={forceEditForm.description ?? ''}
                    onChange={(e) => setForceEditForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                    data-testid="force-edit-field-description"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setForceEditModal(null); setForceEditForm({}); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={executeForceEdit}
                  disabled={!!actionLoading}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="force-edit-save-btn"
                >
                  {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Force Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= HARD DELETE MODAL (typed business-name confirmation) ============= */}
      {hardDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="hard-delete-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" /> Permanently delete business
                </h3>
                <button onClick={() => { setHardDeleteModal(null); setHardDeleteConfirmText(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900">
                This will permanently delete <strong>{hardDeleteModal.businessName}</strong> and ALL associated events, offers, reviews, claims, boost payments, favourites and analytics. <strong>This cannot be undone.</strong>
              </div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type the business name exactly to confirm:
              </label>
              <input
                type="text"
                value={hardDeleteConfirmText}
                onChange={(e) => setHardDeleteConfirmText(e.target.value)}
                placeholder={hardDeleteModal.businessName}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none mb-4"
                data-testid="hard-delete-confirm-input"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setHardDeleteModal(null); setHardDeleteConfirmText(''); }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={executeHardDelete}
                  disabled={!!actionLoading || hardDeleteConfirmText.trim() !== hardDeleteModal.businessName}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="hard-delete-confirm-btn"
                >
                  {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ============= TRIAL FAST-FORWARD MODAL (TEST ACCOUNT ONLY) ============= */}
      {trialFastForwardBiz && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-fuchsia-800" data-testid="trial-fast-forward-title">
                  ⚡ Fast-Forward Trial (TEST MODE)
                </h3>
                <button onClick={() => setTrialFastForwardBiz(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-sm text-fuchsia-900">
                <div className="font-semibold mb-1">{trialFastForwardBiz.businessName}</div>
                <div className="text-xs text-fuchsia-700">
                  Owner (test account): <strong>{trialTestStatus.testEmail}</strong>
                </div>
                <div className="text-xs text-fuchsia-700 mt-1">
                  Current trialEnd: <code>{trialFastForwardBiz.trialEnd || 'n/a'}</code>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                This will move <strong>trialEnd</strong> to{' '}
                <strong>{trialFastForwardMinutes} minute(s) from now</strong> and clear the reminder flags.
                The trial housekeeping loop {trialTestStatus.enabled
                  ? 'runs every 60s (TRIAL_TEST_MODE enabled)'
                  : 'runs every 6h — enable TRIAL_TEST_MODE or click "Run housekeeping now"'}.
              </p>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Minutes from now</label>
              <input
                type="number" min="0" max="1440"
                value={trialFastForwardMinutes}
                onChange={e => setTrialFastForwardMinutes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none mb-2"
                data-testid="trial-fast-forward-minutes-input"
              />
              <p className="text-xs text-gray-500 mb-5">
                Use <strong>0</strong> for immediate expiry. Use <strong>2</strong> to see the 2-day reminder fire, etc.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setTrialFastForwardBiz(null)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  data-testid="trial-fast-forward-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={executeTrialFastForward}
                  disabled={trialFastForwardLoading}
                  className="flex-1 px-4 py-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg font-medium disabled:opacity-50"
                  data-testid="trial-fast-forward-confirm"
                >
                  {trialFastForwardLoading ? 'Fast-forwarding…' : 'Fast-forward now'}
                </button>
              </div>
              <button
                onClick={executeTrialRunHousekeepingNow}
                className="w-full mt-3 px-4 py-2 border border-fuchsia-300 text-fuchsia-700 rounded-lg hover:bg-fuchsia-50 text-sm"
                data-testid="trial-run-housekeeping-now"
              >
                Run housekeeping now (skip the 60s wait)
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ============= EVENT CONFIRMATION MODAL ============= */}
      {eventConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {eventConfirm.type === 'approve' ? t('approveEvent') : eventConfirm.type === 'reject' ? t('rejectEvent') : 'Delete event'}
                </h3>
                <button onClick={() => { setEventConfirm(null); setEventRejectReason(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-600 mb-2">
                  {eventConfirm.type === 'approve' ? t('approveEventDesc') : eventConfirm.type === 'reject' ? t('rejectEventDesc') : 'This event will be permanently deleted. This cannot be undone.'}
                </p>
                <p className="font-semibold text-lg text-gray-900">"{eventConfirm.eventTitle}"</p>
                {eventConfirm.type === 'reject' && (
                  <textarea
                    value={eventRejectReason}
                    onChange={(e) => setEventRejectReason(e.target.value)}
                    placeholder={t('enterEventRejectReason')}
                    className="w-full mt-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none"
                    rows={3}
                    data-testid="event-reject-reason-input"
                  />
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setEventConfirm(null); setEventRejectReason(''); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  {t('cancel')}
                </button>
                {eventConfirm.type === 'approve' && (
                  <button
                    onClick={executeEventApprove}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-approve-event-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    {actionLoading ? t('approving') : t('yesApproveEvent')}
                  </button>
                )}
                {eventConfirm.type === 'reject' && (
                  <button
                    onClick={executeEventReject}
                    disabled={actionLoading || !eventRejectReason.trim()}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-reject-event-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    {actionLoading ? t('rejecting') : t('rejectEvent')}
                  </button>
                )}
                {eventConfirm.type === 'delete' && (
                  <button
                    onClick={executeEventDelete}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-delete-event-btn"
                  >
                    {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Delete Event
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= EVENT EDIT MODAL ============= */}
      {eventEditModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-event-edit-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Edit Event — {eventEditModal.title}</h3>
                <button onClick={() => setEventEditModal(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={eventEditModal.title || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-title" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={3} value={eventEditModal.description || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                    data-testid="event-edit-description" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start (ISO)</label>
                  <input type="text" value={eventEditModal.startDate || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-start" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End (ISO)</label>
                  <input type="text" value={eventEditModal.endDate || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-end" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                  <input type="text" value={eventEditModal.location || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-location" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <input type="text" value={eventEditModal.category || ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-category" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Price (€)</label>
                  <input type="number" value={eventEditModal.price ?? ''}
                    onChange={(e) => setEventEditModal(m => ({ ...m, price: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="event-edit-price" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setEventEditModal(null)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={saveEventEdit}
                  disabled={!!actionLoading}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="event-edit-save-btn"
                >
                  {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Save Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* ============= CHARITIES TAB ============= */}
        {activeTab === 'charities' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200 flex justify-between items-center pr-4">
              <nav className="flex -mb-px">
                {['pending', 'needs_more_info', 'approved', 'rejected', 'all'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setCharityFilter(f); setPageFor('charities', 1); }}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      charityFilter === f
                        ? 'border-teal-600 text-teal-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`charity-filter-${f}`}
                  >
                    {f === 'pending' && t('charity.pendingApplications')}
                    {f === 'needs_more_info' && t('charity.needsInfoApplications')}
                    {f === 'approved' && t('charity.approvedApplications')}
                    {f === 'rejected' && t('charity.rejectedApplications')}
                    {f === 'all' && t('charity.allApplications')}
                  </button>
                ))}
              </nav>
              <button onClick={fetchCharityApps} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {charityLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-teal-600" />
                  <p className="mt-2 text-gray-500">{t('charity.loadingApplications')}</p>
                </div>
              ) : charityApps.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">{t('charity.noApplications')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {charityApps.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-5" data-testid={`charity-app-${app.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{app.organisationName}</h3>
                          <p className="text-sm text-gray-500">{getOrgTypeLabel(app.organisationType)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(app.status)}
                        </div>
                      </div>

                      {/* Application Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-sm">
                        <div className="p-3 bg-teal-50 rounded-lg">
                          <p className="text-xs font-semibold text-teal-700 mb-2 uppercase tracking-wide">{t('charity.orgDetailsTitle')}</p>
                          <p><span className="text-gray-500">{t('charity.contactName')}:</span> <span className="font-medium">{app.contactName}</span></p>
                          <p><span className="text-gray-500">{t('email')}:</span> <span className="font-medium">{app.email}</span></p>
                          <p><span className="text-gray-500">{t('phoneNumber')}:</span> <span className="font-medium">{app.phone || '—'}</span></p>
                          <p><span className="text-gray-500">{t('charity.websiteSocial')}:</span> <span className="font-medium">{app.website || '—'}</span></p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('charity.aboutOrgTitle')}</p>
                          <p><span className="text-gray-500">{t('charity.areaServedLabel')}:</span> <span className="font-medium">{app.areaServed}</span></p>
                          <p><span className="text-gray-500">{t('charity.regNumberLabel')}:</span> <span className="font-medium">{app.registrationNumber || '—'}</span></p>
                          <p><span className="text-gray-500">{t('date')}:</span> <span className="font-medium">{new Date(app.createdAt).toLocaleDateString()}</span></p>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">{t('description')}:</span>
                        <p className="text-sm text-gray-700 mt-1">{app.description}</p>
                      </div>

                      {/* Proof Documents */}
                      {(app.proofFileUrl || app.proofUrl) && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">{t('charity.proofTitle')}</p>
                          <div className="flex flex-wrap gap-2">
                            {app.proofFileUrl && (
                              <a href={app.proofFileUrl} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-teal-600 hover:bg-teal-50 transition-colors"
                                data-testid={`charity-proof-link-${app.id}`}
                              >
                                <FileText className="h-3.5 w-3.5" />
                                {t('charity.viewProof')}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                            {app.proofUrl && (
                              <a href={app.proofUrl} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-teal-600 hover:bg-teal-50 transition-colors"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Proof URL
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Admin Notes */}
                      {app.adminNotes && (
                        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <span className="text-sm text-yellow-700 font-medium">{t('adminNotes')}:</span>
                          <p className="text-sm text-yellow-800 mt-1">{app.adminNotes}</p>
                        </div>
                      )}

                      {/* Created business link */}
                      {app.businessId && (
                        <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <span className="text-sm text-green-700 font-medium">Listing Created:</span>
                          <a href={`/admin/business/${app.businessId}`} className="ml-2 text-sm text-green-600 underline hover:text-green-800">
                            View Business Listing
                          </a>
                        </div>
                      )}

                      {/* Action buttons */}
                      {(app.status === 'pending' || app.status === 'needs_more_info') && (
                        <div className="pt-3 border-t border-gray-200">
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('adminNotes')}</label>
                            <textarea
                              value={charityConfirm?.id === app.id ? charityAdminNotes : ''}
                              onChange={e => { setCharityConfirm({ id: app.id }); setCharityAdminNotes(e.target.value); }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                              rows={2}
                              placeholder="Optional notes..."
                              data-testid={`charity-notes-${app.id}`}
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleCharityApprove(app.id)}
                              disabled={actionLoading === `approve-charity-${app.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              data-testid={`approve-charity-btn-${app.id}`}
                            >
                              {actionLoading === `approve-charity-${app.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              {t('charity.approveApplication')}
                            </button>
                            <button
                              onClick={() => handleCharityReject(app.id)}
                              disabled={actionLoading === `reject-charity-${app.id}`}
                              className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                              data-testid={`reject-charity-btn-${app.id}`}
                            >
                              {actionLoading === `reject-charity-${app.id}` ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                              {t('charity.rejectApplication')}
                            </button>
                            {app.status !== 'needs_more_info' && (
                              <button
                                onClick={() => handleCharityNeedsInfo(app.id)}
                                disabled={actionLoading === `info-charity-${app.id}`}
                                className="flex items-center gap-1 px-4 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                                data-testid={`needs-info-charity-btn-${app.id}`}
                              >
                                {t('charity.needsMoreInfo')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <PaginationControls tab="charities" />
            </div>
          </div>
        )}

        {/* ============= OUTREACH CRM TAB ============= */}
        {activeTab === 'outreach' && <AdminOutreach />}
        {activeTab === 'classifieds' && <AdminClassifieds />}
        {activeTab === 'invoices' && <AdminInvoices />}
        {activeTab === 'referrals' && flags.affiliatesEnabled && <AdminReferrals />}
        {activeTab === 'users' && <AdminUsers />}
        {activeTab === 'issues' && <AdminIssueLog />}
        {activeTab === 'errors' && <AdminErrorLog />}
        {activeTab === 'regressions' && <AdminRegressionLog />}

        {/* Iter 197 — QR generator modal for the Businesses tab. The claim
            variant fires automatically when the listing is still seeded /
            unclaimed so the printable poster reads "Claim this listing". */}
        <BusinessQRModal
          open={!!qrBusiness}
          onClose={() => setQrBusiness(null)}
          businessId={qrBusiness?.id}
          businessName={qrBusiness?.businessName}
          businessLogo={qrBusiness?.logo || qrBusiness?.photos?.[0]}
          businessCity={qrBusiness?.city}
          claim={!!qrBusiness && (qrBusiness.ownershipStatus === 'seeded' || (qrBusiness.source === 'seed' && qrBusiness.isClaimed === false))}
        />

        {/* Feb 2026 — Business Management: shared Change-Email modal.
            Backend endpoint is /admin/users/{ownerUserId}/change-email so the
            audit trail is identical to the Users tab. */}
        <ChangeEmailModal
          open={!!changeEmailTarget}
          target={changeEmailTarget}
          onClose={() => setChangeEmailTarget(null)}
          onError={(msg) => flashMessage('error', msg)}
          onCompleted={(data) => {
            const rd = data?.resetDelivery || {};
            let msg = `Email changed ${data.oldEmail} → ${data.newEmail}.`;
            if (rd.delivered) {
              msg += ` Password-reset link accepted by the email provider.`;
            } else {
              msg += ` ⚠ Password-reset delivery FAILED (${rd.deliveryStatus || 'unknown'}): ${rd.providerResponse || 'no response'}. Retry from Send Reset.`;
            }
            flashMessage(rd.delivered ? 'success' : 'error', msg);
            fetchBusinesses();
          }}
        />

    </div>
  );
};

export default AdminDashboard;
